import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  createUser,
  confirmUserImport,
  deactivateUser,
  listCompanies,
  listDepartmentUsers,
  listDepartments,
  listRequestTypes,
  listRoutingDepartments,
  listUsers,
  previewUserImport,
  resetUserPassword,
  updateUser,
  type Company,
  type Department,
  type ManagedUser,
  type RequestType,
  type UserCreatePayload,
  type UserImportCandidate,
  type UserImportRowResult,
  type UserPasswordResetResponse,
  type UserUpdatePayload,
} from "./api/admin";
import { changePassword, getCurrentUser, login, logout, type CurrentUser } from "./api/auth";
import {
  getAdminDashboard,
  getCustomerDashboard,
  getStaffDashboard,
  type AdminDashboard,
  type CustomerDashboard,
  type RequesterDashboard,
  type StaffDashboard,
} from "./api/dashboard";
import {
  addTicketComment,
  assignTicket,
  classifyTicket,
  issueChargeableRequest,
  respondToChargeableRequest,
  closeTicket,
  createTicket,
  getTicket,
  listStaffAssignedTickets,
  listStaffAvailableTickets,
  listStaffSubmittedTickets,
  listTicketActivity,
  listTicketComments,
  listTickets,
  reassignTicket,
  reopenTicket,
  resolveTicket,
  returnInProgress,
  setWaitingInternal,
  setWaitingRequester,
  startTicket,
  takeTicket,
  unassignTicket,
  type AssignTicketPayload,
  type ChargeableResponsePayload,
  type ClassifyTicketPayload,
  type CommercialClassification,
  type ReassignTicketPayload,
  type TicketActivity,
  type TicketComment,
  TicketApiError,
  type TicketCreatePayload,
  type TicketDetail,
  type TicketFilters,
  type TicketListItem,
  type WorkflowActionResponse,
} from "./api/tickets";
import {
  deleteTicketAttachment,
  downloadTicketAttachment,
  listTicketAttachments,
  uploadTicketAttachment,
  AttachmentApiError,
  type TicketAttachment,
} from "./api/attachments";

import MasterDataPage from "./admin/master-data/MasterDataPage";

type PageKey =
  | "requester-dashboard"
  | "staff-dashboard"
  | "admin-dashboard"
  | "create-ticket"
  | "my-tickets"
  | "my-assigned-tickets"
  | "available-tickets"
  | "all-tickets"
  | "user-management"
  | "master-data"
  | "ticket-detail"
  | "users"
  | "profile";

type NavItem = {
  label: string;
  page: PageKey;
};

type WorkflowAction =
  | { type: "assign"; payload: AssignTicketPayload }
  | { type: "take" }
  | { type: "reassign"; payload: ReassignTicketPayload }
  | { type: "unassign" }
  | { type: "start" }
  | { type: "waiting-requester"; reason: string }
  | { type: "waiting-internal"; reason: string }
  | { type: "return-in-progress" }
  | { type: "resolve"; resolutionComment: string }
  | { type: "close" }
  | { type: "reopen"; reason: string }
  | { type: "classify"; payload: ClassifyTicketPayload }
  | { type: "chargeable-request"; explanation: string };

const navByRole: Record<string, NavItem[]> = {
  Requester: [
    { label: "Dashboard", page: "requester-dashboard" },
    { label: "Create Ticket", page: "create-ticket" },
    { label: "Profile", page: "profile" },
  ],
  Staff: [
    { label: "Dashboard", page: "staff-dashboard" },
    { label: "My Tickets", page: "my-assigned-tickets" },
    { label: "Create Ticket", page: "create-ticket" },
    { label: "Profile", page: "profile" },
  ],
  Admin: [
    { label: "Dashboard", page: "admin-dashboard" },
    { label: "User Management", page: "user-management" },
    { label: "Master Data", page: "master-data" },
    { label: "Profile", page: "profile" },
  ],
};

const pageTitles: Record<PageKey, string> = {
  "requester-dashboard": "Dashboard",
  "staff-dashboard": "Staff Dashboard",
  "admin-dashboard": "Admin Dashboard",
  "create-ticket": "Create Ticket",
  "my-tickets": "My Tickets",
  "my-assigned-tickets": "My Tickets",
  "available-tickets": "Available Tickets",
  "all-tickets": "All Tickets",
  "user-management": "User Management",
  "master-data": "Master Data",
  "ticket-detail": "Ticket Detail",
  users: "Users",
  profile: "Profile",
};

const ticketPages: PageKey[] = ["my-tickets", "my-assigned-tickets", "available-tickets", "all-tickets"];
const dashboardPages: PageKey[] = ["requester-dashboard", "staff-dashboard", "admin-dashboard"];

type DashboardData = CustomerDashboard | RequesterDashboard | StaffDashboard | AdminDashboard;

type DashboardCard = {
  label: string;
  value: number;
  page?: PageKey;
  filters?: TicketFilters;
};

type CustomerTicketFilter = "total" | "open" | "in_progress" | "resolved";
type StaffTicketTab = "assigned" | "available" | "submitted";

function defaultPageForRole(role: string): PageKey {
  if (role === "Admin") {
    return "admin-dashboard";
  }
  if (role === "Staff") {
    return "staff-dashboard";
  }
  return "requester-dashboard";
}

export function canAccessUserManagement(role: string | undefined): boolean {
  return role === "Admin";
}

type SelectedAttachment = {
  id: string;
  file: File;
  name: string;
  size: number;
  lastModified: number;
  type: string;
};
type CreateSubmitPhase = "creating" | "uploading";
type CreateUploadProgress = {
  current: number;
  total: number;
  name: string;
};
type CreateTicketNotice = {
  ticketId: number;
  message: string;
  tone: "ok" | "warn";
};

function buildCreateAttachmentId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `create-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function attachmentMetadataKey(file: File): string {
  return [file.name, file.size, file.lastModified, file.type].join("|");
}

function createPluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

export type AppProps = {
  bizcentralMode?: boolean;
  bizcentralUser?: CurrentUser;
  onBizcentralLogout?: () => void;
};

function App({ bizcentralMode = false, bizcentralUser, onBizcentralLogout }: AppProps = {}) {
  const [user, setUser] = useState<CurrentUser | null>(bizcentralMode ? bizcentralUser ?? null : null);
  const [activePage, setActivePage] = useState<PageKey>(() =>
    bizcentralMode && bizcentralUser
      ? defaultPageForRole(bizcentralUser.role)
      : "requester-dashboard",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(!bizcentralMode);
  const [managedUsers, setManagedUsers] = useState<ManagedUser[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [routingDepartments, setRoutingDepartments] = useState<Department[]>([]);
  const [requestTypes, setRequestTypes] = useState<RequestType[]>([]);
  const [adminMessage, setAdminMessage] = useState("");
  const [adminError, setAdminError] = useState("");
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [ticketComments, setTicketComments] = useState<TicketComment[]>([]);
  const [ticketActivity, setTicketActivity] = useState<TicketActivity[]>([]);
  const [ticketFilters, setTicketFilters] = useState<TicketFilters>({});
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketError, setTicketError] = useState("");
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [dashboardError, setDashboardError] = useState("");
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [isTicketListLoading, setIsTicketListLoading] = useState(false);
  const [customerStatusFilter, setCustomerStatusFilter] = useState<CustomerTicketFilter>("total");
  const [createNotice, setCreateNotice] = useState<CreateTicketNotice | null>(null);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createSubmitPhase, setCreateSubmitPhase] = useState<CreateSubmitPhase | null>(null);
  const [createUploadProgress, setCreateUploadProgress] = useState<CreateUploadProgress | null>(null);
  const [createFilesRevision, setCreateFilesRevision] = useState(0);

  function clearTicketNotices() {
    setTicketMessage("");
    setTicketError("");
    setCreateNotice(null);
  }

  useEffect(() => {
    if (bizcentralMode) {
      setUser(bizcentralUser ?? null);
      setIsLoading(false);
      return;
    }
    getCurrentUser()
      .then((currentUser) => {
        setUser(currentUser);
        setActivePage(defaultPageForRole(currentUser.role));
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, [bizcentralMode, bizcentralUser]);

  const navItems = useMemo(() => (user ? navByRole[user.role] ?? navByRole.Requester : []), [user]);
  const isCustomerUser = user?.role === "Requester";
  const isStaffUser = user?.role === "Staff";
  const isAdminUser = user?.role === "Admin";
  const usesPortalShell = isCustomerUser || isStaffUser || isAdminUser;
  const currentCompanyName = companies.find((company) => company.id === user?.company_id)?.name ?? "-";
  const currentDepartmentName = departments.find((department) => department.id === user?.department_id)?.name ?? "-";

  useEffect(() => {
    if (!user || activePage !== "user-management") {
      return;
    }

    if (!canAccessUserManagement(user.role)) {
      return;
    }

    refreshUserManagement();
  }, [activePage, user]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!dashboardPages.includes(activePage)) {
      return;
    }
    const currentUser = user;

    async function loadDashboard() {
      setDashboardError("");
      setIsDashboardLoading(true);
      try {
        if (currentUser.role === "Requester" && activePage === "requester-dashboard") {
          setDashboardData(await getCustomerDashboard());
        } else if (currentUser.role === "Staff" && activePage === "staff-dashboard") {
          setDashboardData(await getStaffDashboard());
        } else if (currentUser.role === "Admin" && activePage === "admin-dashboard") {
          setDashboardData(await getAdminDashboard());
        } else {
          setDashboardError("Dashboard unavailable for this role.");
          setDashboardData(null);
        }
      } catch {
        setDashboardError("Unable to load dashboard.");
        setDashboardData(null);
      } finally {
        setIsDashboardLoading(false);
      }
    }

    loadDashboard();
  }, [activePage, user]);

  useEffect(() => {
    if (!user) {
      return;
    }
    const currentUser = user;

    async function loadTicketPageData() {
      if (
        activePage === "create-ticket" ||
        activePage === "ticket-detail" ||
        activePage === "profile" ||
        activePage === "requester-dashboard" ||
        activePage === "staff-dashboard" ||
        activePage === "admin-dashboard" ||
        activePage === "users" ||
        ticketPages.includes(activePage)
      ) {
        try {
          const [
            companiesResponse,
            departmentsResponse,
            routingDepartmentsResponse,
            requestTypesResponse,
          ] = await Promise.all([
            listCompanies(),
            listDepartments(),
            listRoutingDepartments(),
            listRequestTypes(),
          ]);
          setCompanies(companiesResponse);
          setDepartments(departmentsResponse);
          setRoutingDepartments(routingDepartmentsResponse);
          setRequestTypes(requestTypesResponse);
        } catch {
          setTicketError("Unable to load form data.");
        }
      }

      if (activePage === "requester-dashboard" && currentUser.role === "Requester") {
        setTicketError("");
        setIsTicketListLoading(true);
        try {
          setTickets(await listTickets());
        } catch {
          setTicketError("Unable to load your support tickets.");
        } finally {
          setIsTicketListLoading(false);
        }
      }

      if (activePage === "staff-dashboard" && currentUser.role === "Staff") {
        setTicketError("");
        setIsTicketListLoading(true);
        try {
          const [assignedTicketsResponse, departmentUsersResponse] = await Promise.all([
            listStaffAssignedTickets(),
            listDepartmentUsers(),
          ]);
          setTickets(assignedTicketsResponse);
          setManagedUsers(departmentUsersResponse);
        } catch {
          setTicketError("Unable to load assigned tickets.");
        } finally {
          setIsTicketListLoading(false);
        }
      }

      if (activePage === "admin-dashboard" && currentUser.role === "Admin") {
        setTicketError("");
        setIsTicketListLoading(true);
        try {
          const [ticketsResponse, usersResponse] = await Promise.all([listTickets(ticketFilters), listUsers()]);
          setTickets(ticketsResponse);
          setManagedUsers(usersResponse);
        } catch {
          setTicketError("Unable to load admin dashboard data.");
        } finally {
          setIsTicketListLoading(false);
        }
      }

      if (activePage === "users" && currentUser.role === "Staff") {
        setTicketError("");
        try {
          setManagedUsers(await listDepartmentUsers());
        } catch {
          setTicketError("Unable to load department users.");
        }
      }

      if (activePage === "ticket-detail" && currentUser.role === "Admin") {
        try {
          setManagedUsers(await listUsers());
        } catch {
          setTicketError("Unable to load assignment users.");
        }
      }

      if (activePage === "ticket-detail" && currentUser.role === "Staff") {
        try {
          setManagedUsers(await listDepartmentUsers());
        } catch {
          setTicketError("Unable to load department users.");
        }
      }

      if (ticketPages.includes(activePage)) {
        setTicketError("");
        setIsTicketListLoading(true);
        try {
          if (currentUser.role === "Staff" && activePage === "my-assigned-tickets") {
            setTickets(await listStaffAssignedTickets());
          } else if (currentUser.role === "Staff" && activePage === "available-tickets") {
            setTickets(await listStaffAvailableTickets());
          } else if (currentUser.role === "Staff" && activePage === "my-tickets") {
            setTickets(await listStaffSubmittedTickets());
          } else {
            setTickets(await listTickets(ticketFilters));
          }
        } catch {
          setTicketError("Unable to load tickets.");
        } finally {
          setIsTicketListLoading(false);
        }
      }
    }

    loadTicketPageData();
  }, [activePage, ticketFilters, user]);

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    try {
      const loggedInUser = await login(email, password);
      setPassword("");
      clearTicketNotices();
      setUser(loggedInUser);
      if (loggedInUser.must_change_password === true) {
        // Forced-change guard takes over the render; skip role-based landing.
        return;
      }
      setActivePage(defaultPageForRole(loggedInUser.role));
    } catch {
      setError("Invalid login or inactive account.");
    }
  }

  function handleForcedPasswordChangeSuccess() {
    if (!user) return;
    setUser({ ...user, must_change_password: false });
    setActivePage(defaultPageForRole(user.role));
  }

  async function handleLogout() {
    if (bizcentralMode) {
      clearTicketNotices();
      setUser(null);
      setActivePage("requester-dashboard");
      onBizcentralLogout?.();
      return;
    }
    await logout();
    clearTicketNotices();
    setUser(null);
    setActivePage("requester-dashboard");
  }

  async function refreshUserManagement() {
    setAdminError("");
    try {
      const [usersResponse, companiesResponse, departmentsResponse, requestTypesResponse] = await Promise.all([
        listUsers(),
        listCompanies(),
        listDepartments(),
        listRequestTypes(),
      ]);
      setManagedUsers(usersResponse);
      setCompanies(companiesResponse);
      setDepartments(departmentsResponse);
      setRequestTypes(requestTypesResponse);
    } catch {
      setAdminError("Unable to load user management data.");
    }
  }

  function mapCreateTicketError(caught: unknown): { message: string; staleCategory: boolean } {
    if (caught instanceof TicketApiError) {
      switch (caught.errorCode) {
        case "CATEGORY_NOT_VISIBLE":
          return {
            message: "This Request Category is no longer available for your account. Please select another category.",
            staleCategory: true,
          };
        case "CATEGORY_INACTIVE":
          return {
            message: "This Request Category is inactive. Please select another category.",
            staleCategory: true,
          };
        case "CATEGORY_ROUTING_UNAVAILABLE":
          return {
            message: "This Request Category is temporarily unavailable for routing. Please select another category or contact support.",
            staleCategory: true,
          };
        case "CATEGORY_NOT_FOUND":
          return {
            message: "The selected Request Category could not be found. Please refresh and try again.",
            staleCategory: true,
          };
        case "INVALID_PRIORITY":
          return {
            message: "Please select a valid priority.",
            staleCategory: false,
          };
        default:
          return { message: caught.detail || "Unable to create ticket. Please try again.", staleCategory: false };
      }
    }
    return {
      message: "Unable to create ticket. Please check your connection and try again.",
      staleCategory: false,
    };
  }

  async function handleCreateTicket(
    payload: TicketCreatePayload,
    uploadSnapshot: File[],
  ): Promise<{ ok: boolean; staleCategory: boolean }> {
    setTicketMessage("");
    setTicketError("");
    setCreateNotice(null);
    setCreateSubmitting(true);
    setCreateSubmitPhase("creating");
    setCreateUploadProgress(null);
    let ticketRef: TicketDetail;
    try {
      ticketRef = await createTicket(payload);
    } catch (caught) {
      setCreateSubmitting(false);
      setCreateSubmitPhase(null);
      const mapped = mapCreateTicketError(caught);
      setTicketError(mapped.message);
      let refreshedRequestTypes: Awaited<ReturnType<typeof listRequestTypes>> | null = null;
      if (mapped.staleCategory) {
        try {
          refreshedRequestTypes = await listRequestTypes();
        } catch {
          // Best-effort refresh; the in-page notice already guides the user.
        }
        if (refreshedRequestTypes !== null) {
          setRequestTypes(refreshedRequestTypes);
        }
      }
      return { ok: false, staleCategory: mapped.staleCategory };
    }

    setCreateFilesRevision((value) => value + 1);

    const total = uploadSnapshot.length;
    let successCount = 0;
    let failureCount = 0;
    const failedNames: string[] = [];

    if (total === 0) {
      setCreateSubmitPhase(null);
      setCreateUploadProgress(null);
      setCreateSubmitting(false);
      setTicketMessage(`Ticket ${ticketRef.ticket_no} created.`);
      setCreateNotice({
        ticketId: ticketRef.id,
        message: "Ticket created successfully.",
        tone: "ok",
      });
    } else {
      setCreateSubmitPhase("uploading");
      for (let index = 0; index < total; index += 1) {
        const file = uploadSnapshot[index];
        setCreateUploadProgress({ current: index + 1, total, name: file.name });
        try {
          await uploadTicketAttachment(ticketRef.id, file);
          successCount += 1;
        } catch {
          failureCount += 1;
          failedNames.push(file.name);
        }
      }

      setCreateUploadProgress(null);
      setCreateSubmitting(false);
      setCreateSubmitPhase(null);

      const totalCount = total;
      const allOk = failureCount === 0;
      const allFailed = successCount === 0;
      const noun = createPluralize(totalCount, "attachment", "attachments");
      let noticeMessage: string;
      let noticeTone: "ok" | "warn";
      if (allOk) {
        noticeMessage = `Ticket created successfully. All ${totalCount} ${noun} were uploaded.`;
        noticeTone = "ok";
      } else if (allFailed) {
        noticeMessage = `Ticket created successfully, but none of the ${totalCount} ${noun} could be uploaded. The ticket still exists.`;
        noticeTone = "warn";
      } else {
        noticeMessage = `Ticket created successfully. ${successCount} of ${totalCount} ${noun} were uploaded. The ticket still exists, but some attachments could not be uploaded.`;
        noticeTone = "warn";
      }
      setCreateNotice({ ticketId: ticketRef.id, message: noticeMessage, tone: noticeTone });
      if (failedNames.length > 0) {
        setTicketError(`Some attachments could not be uploaded: ${failedNames.join(", ")}.`);
      } else {
        setTicketMessage(`Ticket ${ticketRef.ticket_no} created. All attachments uploaded.`);
      }
    }

    setSelectedTicket(ticketRef);
    setActivePage("ticket-detail");

    try {
      await refreshTicketTimeline(ticketRef.id);
    } catch {
      // Timeline refresh is best effort; preserve creation and upload results.
    }
    return { ok: true, staleCategory: false };
  }

  async function handleViewTicket(ticketId: number) {
    setTicketError("");
    setCreateNotice((current) => {
      if (current !== null && current.ticketId !== ticketId) {
        return null;
      }
      return current;
    });
    try {
      setSelectedTicket(await getTicket(ticketId));
      await refreshTicketTimeline(ticketId);
      setActivePage("ticket-detail");
    } catch {
      setTicketError("Unable to open ticket.");
    }
  }

  async function refreshTicketTimeline(ticketId: number) {
    const [commentsResponse, activityResponse] = await Promise.all([
      listTicketComments(ticketId),
      listTicketActivity(ticketId),
    ]);
    setTicketComments(commentsResponse);
    setTicketActivity(activityResponse);
  }

  async function handleAddTicketComment(
    ticketId: number,
    commentText: string,
    isInternal: boolean = false,
  ) {
    setTicketMessage("");
    setTicketError("");
    try {
      await addTicketComment(ticketId, commentText, isInternal);
      setTicketMessage("Comment added.");
      setSelectedTicket(await getTicket(ticketId));
      await refreshTicketTimeline(ticketId);
    } catch {
      setTicketError("Unable to add comment.");
    }
  }

  async function handleRespondToChargeableRequest(
    ticketId: number,
    payload: ChargeableResponsePayload,
  ): Promise<WorkflowActionResponse> {
    const response = await respondToChargeableRequest(ticketId, payload);
    setSelectedTicket(response.ticket);
    setTickets((currentTickets) =>
      currentTickets.map((t) => (t.id === response.ticket.id ? response.ticket : t)),
    );
    setTicketMessage(response.message);
    await refreshTicketTimeline(ticketId);
    return response;
  }

  async function handleWorkflowAction(ticketId: number, action: WorkflowAction) {
    setTicketMessage("");
    setTicketError("");
    try {
      let response: WorkflowActionResponse;
      switch (action.type) {
        case "assign":
          response = await assignTicket(ticketId, action.payload);
          break;
        case "take":
          response = await takeTicket(ticketId);
          break;
        case "reassign":
          response = await reassignTicket(ticketId, action.payload);
          break;
        case "unassign":
          response = await unassignTicket(ticketId);
          break;
        case "start":
          response = await startTicket(ticketId);
          break;
        case "waiting-requester":
          response = await setWaitingRequester(ticketId, action.reason);
          break;
        case "waiting-internal":
          response = await setWaitingInternal(ticketId, action.reason);
          break;
        case "return-in-progress":
          response = await returnInProgress(ticketId);
          break;
        case "resolve":
          response = await resolveTicket(ticketId, action.resolutionComment);
          break;
        case "close":
          response = await closeTicket(ticketId);
          break;
        case "reopen":
          response = await reopenTicket(ticketId, action.reason);
          break;
        case "classify":
          response = await classifyTicket(ticketId, action.payload);
          break;
        case "chargeable-request":
          response = await issueChargeableRequest(ticketId, { chargeable_explanation: action.explanation });
          break;
      }

      setSelectedTicket(response.ticket);
      setTickets((currentTickets) =>
        currentTickets.map((ticket) => (ticket.id === response.ticket.id ? response.ticket : ticket)),
      );
      setTicketMessage(response.message);
      await refreshTicketTimeline(ticketId);
    } catch (err: unknown) {
      const detail =
        err instanceof TicketApiError ? err.detail : "Unable to update ticket workflow.";
      setTicketError(detail);
      throw err;
    }
  }

  function handleOpenTicketList(page: PageKey, filters: TicketFilters = {}) {
    setTicketFilters(filters);
    setActivePage(page);
  }

  async function handleCreateUser(payload: UserCreatePayload) {
    setAdminMessage("");
    setAdminError("");
    try {
      await createUser(payload);
      setAdminMessage("User created.");
      await refreshUserManagement();
    } catch {
      setAdminError("Unable to create user. Check the email and required fields.");
    }
  }

  async function handleDeactivateUser(userId: number) {
    setAdminMessage("");
    setAdminError("");
    try {
      await deactivateUser(userId);
      setAdminMessage("User deactivated.");
      await refreshUserManagement();
    } catch {
      setAdminError("Unable to deactivate user.");
    }
  }

  async function handleUpdateUser(userId: number, payload: Partial<UserUpdatePayload>) {
    setAdminMessage("");
    setAdminError("");
    try {
      await updateUser(userId, payload);
      setAdminMessage("User updated.");
      await refreshUserManagement();
    } catch {
      setAdminError("Unable to update user. Check the required fields.");
      throw new Error("Unable to update user.");
    }
  }

  async function handleResetUserPassword(userId: number): Promise<UserPasswordResetResponse> {
    setAdminMessage("");
    setAdminError("");
    const response = await resetUserPassword(userId);
    setAdminMessage("Password reset.");
    return response;
  }

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-6 text-zinc-950">
        <p className="text-sm font-medium text-zinc-600">Loading...</p>
      </main>
    );
  }

  if (!user && bizcentralMode) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-6 text-zinc-950">
        <p className="text-sm font-medium text-zinc-600">Connecting to Bizcentral...</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-6 text-zinc-950">
        <section className="w-full max-w-md rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-wide text-sky-700">MVP PILOT</p>
          <h1 className="mt-3 text-2xl font-semibold">iRadar Ticketing System</h1>
          <form className="mt-8 space-y-5" onSubmit={handleLogin}>
            <label className="block text-sm font-medium text-zinc-700">
              Email
              <input
                className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <label className="block text-sm font-medium text-zinc-700">
              Password
              <input
                className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            {error ? <p className="text-sm text-red-700">{error}</p> : null}
            <button className="w-full rounded-md bg-sky-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-800">
              Login
            </button>
          </form>
        </section>
      </main>
    );
  }

  if (!bizcentralMode && user.must_change_password === true) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f4f5f7] px-6 text-zinc-950">
        <section
          aria-label="Forced password change"
          className="w-full max-w-xl rounded-lg border border-amber-300 bg-white p-8 shadow-sm"
        >
          <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">First-Time Login</p>
          <h1 className="mt-3 text-2xl font-semibold">Change Your Temporary Password</h1>
          <p className="mt-3 text-sm text-zinc-700">
            You are signed in with a temporary password. Please set a new password before you can continue using the
            iRadar Ticketing System.
          </p>
          <div className="mt-6">
            <PasswordChangeSection
              submitLabel="Set New Password"
              onChangeSuccess={handleForcedPasswordChangeSuccess}
            />
          </div>
          <div className="mt-6 border-t border-zinc-200 pt-4">
            <button
              className="text-sm font-semibold text-sky-700 hover:text-sky-800"
              onClick={handleLogout}
              type="button"
            >
              Log Out
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#f4f5f7] text-zinc-950">
      <div className="flex min-h-screen">
        <aside className="w-64 shrink-0 border-r border-zinc-200 bg-white px-4 py-5 shadow-sm">
          <div className="flex items-center gap-3 border-b border-zinc-100 pb-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-zinc-950 text-sm font-bold text-white">
              iR
            </div>
            <div>
              <h1 className="text-base font-bold tracking-tight">iRadar</h1>
              <p className="text-xs font-medium text-zinc-500">Ticketing System</p>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3 rounded-md bg-zinc-50 p-3">
            <AvatarInitials name={user.full_name} />
            <div>
              <p className="text-sm font-semibold text-zinc-900">{user.full_name}</p>
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                {isCustomerUser ? "Customer" : user.role}
              </p>
            </div>
          </div>
          <nav className="mt-6 space-y-1">
            {navItems.map((item) => (
              <button
                className={`w-full rounded-md px-3 py-2 text-left text-sm font-semibold transition ${
                  activePage === item.page
                    ? "bg-zinc-950 text-white shadow-sm"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"
                }`}
                key={item.page}
                onClick={() => {
                  if (ticketPages.includes(item.page)) {
                    setTicketFilters({});
                  }
                  setActivePage(item.page);
                }}
              >
                {item.label}
              </button>
            ))}
            <button
              className="w-full rounded-md px-3 py-2 text-left text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950"
              onClick={handleLogout}
            >
              Log Out
            </button>
          </nav>
        </aside>
        <section className="min-w-0 flex-1">
          <header className="bg-zinc-950 px-8 py-5 text-white">
            <div
              className={`flex w-full items-center justify-between gap-6 ${
                usesPortalShell ? "mx-auto max-w-[1020px]" : ""
              }`}
            >
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-rose-200">
                  {usesPortalShell ? "Support Portal" : "MVP PILOT"}
                </p>
                <h2 className="mt-1 text-2xl font-semibold">
                  {isCustomerUser ? `Welcome back, ${user.full_name}` : pageTitles[activePage]}
                </h2>
                {isStaffUser ? (
                  <p className="mt-1 text-sm font-medium text-zinc-300">
                    {currentDepartmentName !== "-" ? currentDepartmentName : "IT Support"} - {user.full_name}
                  </p>
                ) : null}
                {isAdminUser ? <p className="mt-1 text-sm font-medium text-zinc-300">Admin Portal</p> : null}
              </div>
              <div className="flex items-center gap-4">
                <AvatarInitials name={user.full_name} size="lg" />
              </div>
            </div>
          </header>
          <div className="px-8 py-8">
          <div className={usesPortalShell ? "mx-auto w-full max-w-[1020px]" : ""}>
          {activePage === "requester-dashboard" && isCustomerUser ? (
            <CustomerDashboardPage
              dashboard={dashboardData as CustomerDashboard | null}
              error={dashboardError || ticketError}
              filter={customerStatusFilter}
              isDashboardLoading={isDashboardLoading}
              isTicketListLoading={isTicketListLoading}
              onCreateTicket={() => setActivePage("create-ticket")}
              onFilterChange={setCustomerStatusFilter}
              onViewTicket={handleViewTicket}
              tickets={tickets}
            />
          ) : activePage === "staff-dashboard" && isStaffUser ? (
            <StaffDashboardPage
              dashboard={dashboardData as StaffDashboard | null}
              departmentUsers={managedUsers}
              departments={routingDepartments}
              error={dashboardError || ticketError}
              isDashboardLoading={isDashboardLoading}
              isTicketListLoading={isTicketListLoading}
              onReassignTicket={handleWorkflowAction}
              onResolveTicket={handleWorkflowAction}
              onViewTicket={handleViewTicket}
              tickets={tickets}
              user={user}
            />
          ) : activePage === "admin-dashboard" && isAdminUser ? (
            <AdminDashboardPage
              dashboard={dashboardData as AdminDashboard | null}
              departments={routingDepartments}
              error={dashboardError || ticketError}
              filters={ticketFilters}
              isDashboardLoading={isDashboardLoading}
              isTicketListLoading={isTicketListLoading}
              onAssignTicket={handleWorkflowAction}
              onChangeFilters={setTicketFilters}
              onUnassignTicket={handleWorkflowAction}
              onViewTicket={handleViewTicket}
              tickets={tickets}
              user={user}
              users={managedUsers}
            />
          ) : dashboardPages.includes(activePage) ? (
            <DashboardPage
              data={dashboardData}
              error={dashboardError}
              isLoading={isDashboardLoading}
              onOpenTicketList={handleOpenTicketList}
              page={activePage}
            />
          ) : activePage === "user-management" && canAccessUserManagement(user.role) ? (
            <UserManagementPage
              companies={companies}
              departments={departments}
              error={adminError}
              message={adminMessage}
              onCreateUser={handleCreateUser}
              onImportSuccess={refreshUserManagement}
              onDeactivateUser={handleDeactivateUser}
              onResetUserPassword={handleResetUserPassword}
              onUpdateUser={handleUpdateUser}
              user={user}
              users={managedUsers}
            />
          ) : activePage === "master-data" && isAdminUser ? (
            <MasterDataPage />
          ) : activePage === "create-ticket" ? (
            <CreateTicketPage
              error={ticketError}
              filesRevision={createFilesRevision}
              isCustomer={isCustomerUser || isStaffUser}
              isRequester={isCustomerUser || isStaffUser}
              message={ticketMessage}
              onCancel={() => setActivePage(defaultPageForRole(user.role))}
              onCreateTicket={handleCreateTicket}
              requestTypes={requestTypes}
              submitPhase={createSubmitPhase}
              submitting={createSubmitting}
              uploadProgress={createUploadProgress}
            />
          ) : isStaffUser && ticketPages.includes(activePage) ? (
            <StaffTicketsPage
              activeTab={staffTabForPage(activePage)}
              departmentUsers={managedUsers}
              departments={routingDepartments}
              error={ticketError}
              filters={ticketFilters}
              isLoading={isTicketListLoading}
              onChangeFilters={setTicketFilters}
              onReassignTicket={handleWorkflowAction}
              onResolveTicket={handleWorkflowAction}
              onTabChange={(tab) => setActivePage(pageForStaffTab(tab))}
              onTakeTicket={handleWorkflowAction}
              onViewTicket={handleViewTicket}
              tickets={tickets}
              user={user}
            />
          ) : ticketPages.includes(activePage) ? (
            <TicketListPage
              departments={routingDepartments}
              error={ticketError}
              filters={ticketFilters}
              isLoading={isTicketListLoading}
              onChangeFilters={setTicketFilters}
              onViewTicket={handleViewTicket}
              requestTypes={requestTypes}
              tickets={tickets}
              title={pageTitles[activePage]}
              user={user}
            />
          ) : activePage === "ticket-detail" && selectedTicket ? (
            <TicketDetailPage
              activity={ticketActivity}
              comments={ticketComments}
              createNotice={
                createNotice !== null && createNotice.ticketId === selectedTicket.id
                  ? createNotice
                  : null
              }
              departments={routingDepartments}
              error={ticketError}
              message={ticketMessage}
              onAddComment={handleAddTicketComment}
              onBack={() => {
                setCreateNotice(null);
                setActivePage(defaultPageForRole(user.role));
              }}
              onRefreshTimeline={refreshTicketTimeline}
              onRespondToChargeableRequest={handleRespondToChargeableRequest}
              onWorkflowAction={handleWorkflowAction}
              ticket={selectedTicket}
              user={user}
              users={managedUsers}
            />
          ) : activePage === "users" && isStaffUser ? (
            <DepartmentUsersPage
              departmentName={currentDepartmentName}
              error={ticketError}
              users={managedUsers}
            />
          ) : activePage === "profile" ? (
            <ProfilePage
              allowPasswordChange={!bizcentralMode}
              companyName={currentCompanyName}
              departmentName={currentDepartmentName}
              isCustomer={isCustomerUser}
              user={user}
            />
          ) : (
            <PlaceholderPage title={pageTitles[activePage]} />
          )}
          </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="mt-8 rounded-lg border border-zinc-200 bg-white p-6">
      <p className="text-sm leading-6 text-zinc-600">
        {title} is reserved for the next implementation stage. Authentication, role-based navigation, and basic user
        management are available now.
      </p>
    </div>
  );
}

function CustomerDashboardPage({
  dashboard,
  error,
  filter,
  isDashboardLoading,
  isTicketListLoading,
  onCreateTicket,
  onFilterChange,
  onViewTicket,
  tickets,
}: {
  dashboard: CustomerDashboard | null;
  error: string;
  filter: CustomerTicketFilter;
  isDashboardLoading: boolean;
  isTicketListLoading: boolean;
  onCreateTicket(): void;
  onFilterChange(filter: CustomerTicketFilter): void;
  onViewTicket(ticketId: number): Promise<void>;
  tickets: TicketListItem[];
}) {
  const filteredTickets = tickets.filter((ticket) => customerTicketMatchesFilter(ticket, filter));
  const cards: { key: CustomerTicketFilter; label: string; value: number; tone: string; icon: string }[] = [
    { key: "total", label: "Total Tickets", value: dashboard?.total ?? 0, tone: "bg-zinc-100 text-zinc-700", icon: "T" },
    { key: "open", label: "Open", value: dashboard?.open ?? 0, tone: "bg-rose-50 text-rose-700", icon: "O" },
    { key: "in_progress", label: "In Progress", value: dashboard?.in_progress ?? 0, tone: "bg-amber-50 text-amber-700", icon: "P" },
    { key: "resolved", label: "Resolved", value: dashboard?.resolved ?? 0, tone: "bg-emerald-50 text-emerald-700", icon: "R" },
  ];

  return (
    <section className="space-y-7">
      {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
      <section>
        <div>
          <h3 className="text-2xl font-semibold tracking-tight">Real Time Ticket Tracking</h3>
          <p className="mt-1 text-sm text-zinc-500">Click on any card to filter tickets by status</p>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-4">
          {cards.map((card) => (
            <button
              className={`rounded-lg border bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
                filter === card.key
                  ? "border-zinc-950 ring-2 ring-zinc-950/10"
                  : "border-zinc-200 hover:border-zinc-300"
              }`}
              disabled={isDashboardLoading}
              key={card.key}
              onClick={() => onFilterChange(card.key)}
              type="button"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-zinc-500">{card.label}</p>
                  <p className="mt-2 text-3xl font-bold text-zinc-950">{isDashboardLoading ? "-" : card.value}</p>
                </div>
                <span className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold ${card.tone}`}>
                  {card.icon}
                </span>
              </div>
            </button>
          ))}
        </div>
        <div className="mt-6 text-center">
          <button
            className="rounded-md bg-rose-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-rose-700"
            onClick={onCreateTicket}
            type="button"
          >
            Submit New Ticket
          </button>
          <p className="mt-3 text-sm text-zinc-500">Need help? Submit a support ticket and we'll get back to you soon!</p>
        </div>
      </section>
      <section>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h4 className="text-xl font-semibold tracking-tight">Your Support Tickets</h4>
            <p className="mt-1 text-sm text-zinc-500">Track the status of your submitted tickets</p>
          </div>
          <p className="text-sm font-medium text-zinc-500">{filteredTickets.length} tickets shown</p>
        </div>
        {isTicketListLoading ? <p className="mt-5 rounded-md bg-white p-5 text-sm text-zinc-600">Loading tickets...</p> : null}
        <div className="mt-4 grid gap-3">
          {filteredTickets.map((ticket) => (
            <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" key={ticket.id}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h5 className="text-base font-semibold text-zinc-950">{ticket.title}</h5>
                    <Badge className={statusBadgeClass(ticket.status)}>{customerStatusLabel(ticket.status)}</Badge>
                    <Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge>
                  </div>
                  <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-500">
                    <InlineMeta label="Ticket" value={ticket.ticket_no} />
                    <InlineMeta label="Created" value={formatDate(ticket.created_at)} />
                    <InlineMeta label="Group" value={ticket.owner_department_name ?? "-"} />
                    <InlineMeta label="Service Type" value={ticket.request_type_name ?? "-"} />
                  </dl>
                </div>
                <button
                  className="shrink-0 rounded-md border border-zinc-300 px-4 py-2 text-sm font-semibold text-zinc-800 transition hover:border-zinc-950 hover:bg-zinc-950 hover:text-white"
                  onClick={() => onViewTicket(ticket.id)}
                  type="button"
                >
                  View Details
                </button>
              </div>
            </article>
          ))}
        </div>
        {!isTicketListLoading && filteredTickets.length === 0 ? (
          <p className="mt-5 rounded-lg border border-zinc-200 bg-zinc-50 p-8 text-center text-sm text-zinc-500">
            No support tickets match this status.
          </p>
        ) : null}
      </section>
    </section>
  );
}

function StaffDashboardPage({
  dashboard,
  departmentUsers,
  departments,
  error,
  isDashboardLoading,
  isTicketListLoading,
  onReassignTicket,
  onResolveTicket,
  onViewTicket,
  tickets,
  user,
}: {
  dashboard: StaffDashboard | null;
  departmentUsers: ManagedUser[];
  departments: Department[];
  error: string;
  isDashboardLoading: boolean;
  isTicketListLoading: boolean;
  onReassignTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onResolveTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onViewTicket(ticketId: number): Promise<void>;
  tickets: TicketListItem[];
  user: CurrentUser;
}) {
  const [filters, setFilters] = useState<TicketFilters>({});
  const visibleTickets = filterTicketsForStaff(tickets, filters);
  const closedTickets = tickets.filter((ticket) => ticket.status === "Closed").length;
  const cards = [
    { label: "Total Tickets", value: dashboard?.my_assigned ?? 0, tone: "bg-zinc-100 text-zinc-700", icon: "T" },
    {
      label: "My Open",
      value: Math.max((dashboard?.my_assigned ?? 0) - (dashboard?.resolved ?? 0) - closedTickets, 0),
      tone: "bg-rose-50 text-rose-700",
      icon: "O",
    },
    { label: "In Progress", value: dashboard?.in_progress ?? 0, tone: "bg-amber-50 text-amber-700", icon: "P" },
    { label: "Resolved", value: dashboard?.resolved ?? 0, tone: "bg-emerald-50 text-emerald-700", icon: "R" },
  ];

  return (
    <section className="space-y-7">
      {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
      <section>
        <div>
          <h3 className="text-2xl font-semibold tracking-tight">Real-Time Ticket Tracking</h3>
          <p className="mt-1 text-sm text-zinc-500">Monitor your assigned support work and move tickets forward.</p>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-4">
          {cards.map((card) => (
            <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" key={card.label}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-zinc-500">{card.label}</p>
                  <p className="mt-2 text-3xl font-bold text-zinc-950">{isDashboardLoading ? "-" : card.value}</p>
                </div>
                <span className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold ${card.tone}`}>
                  {card.icon}
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>
      <StaffTicketCardList
        actionMode="assigned"
        departmentUsers={departmentUsers}
        departments={departments}
        emptyText="No assigned tickets match these filters."
        filters={filters}
        isLoading={isTicketListLoading}
        onChangeFilters={setFilters}
        onReassignTicket={onReassignTicket}
        onResolveTicket={onResolveTicket}
        onViewTicket={onViewTicket}
        tickets={visibleTickets}
        title="My Assigned Tickets"
        user={user}
      />
    </section>
  );
}

function StaffTicketsPage({
  activeTab,
  departmentUsers,
  departments,
  error,
  filters,
  isLoading,
  onChangeFilters,
  onReassignTicket,
  onResolveTicket,
  onTabChange,
  onTakeTicket,
  onViewTicket,
  tickets,
  user,
}: {
  activeTab: StaffTicketTab;
  departmentUsers: ManagedUser[];
  departments: Department[];
  error: string;
  filters: TicketFilters;
  isLoading: boolean;
  onChangeFilters(filters: TicketFilters): void;
  onReassignTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onResolveTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onTabChange(tab: StaffTicketTab): void;
  onTakeTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onViewTicket(ticketId: number): Promise<void>;
  tickets: TicketListItem[];
  user: CurrentUser;
}) {
  const visibleTickets = filterTicketsForStaff(tickets, filters);
  const tabs: { key: StaffTicketTab; label: string }[] = [
    { key: "assigned", label: "Assigned Tickets" },
    { key: "available", label: "Available Tickets" },
    { key: "submitted", label: "My Tickets" },
  ];

  return (
    <section className="space-y-5">
      <div>
        <h3 className="text-2xl font-semibold tracking-tight">My Tickets</h3>
        <p className="mt-1 text-sm text-zinc-500">
          Review assigned work, pick up available tickets, and track requests you submitted.
        </p>
      </div>
      {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
      <div className="flex flex-wrap gap-2 rounded-lg border border-zinc-200 bg-white p-2 shadow-sm">
        {tabs.map((tab) => (
          <button
            className={`rounded-md px-4 py-2 text-sm font-semibold transition ${
              activeTab === tab.key ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"
            }`}
            key={tab.key}
            onClick={() => {
              onChangeFilters({ classification: filters.classification });
              onTabChange(tab.key);
            }}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>
      <StaffTicketCardList
        actionMode={activeTab}
        departmentUsers={departmentUsers}
        departments={departments}
        emptyText={staffEmptyText(activeTab)}
        filters={filters}
        isLoading={isLoading}
        onChangeFilters={onChangeFilters}
        onReassignTicket={onReassignTicket}
        onResolveTicket={onResolveTicket}
        onTakeTicket={onTakeTicket}
        onViewTicket={onViewTicket}
        tickets={visibleTickets}
        title={tabs.find((tab) => tab.key === activeTab)?.label ?? "Tickets"}
        user={user}
      />
    </section>
  );
}

function StaffTicketCardList({
  actionMode,
  departmentUsers = [],
  departments = [],
  emptyText,
  filters,
  isLoading,
  onChangeFilters,
  onReassignTicket,
  onResolveTicket,
  onTakeTicket,
  onViewTicket,
  tickets,
  title,
  user,
}: {
  actionMode: StaffTicketTab;
  departmentUsers?: ManagedUser[];
  departments?: Department[];
  emptyText: string;
  filters: TicketFilters;
  isLoading: boolean;
  onChangeFilters(filters: TicketFilters): void;
  onReassignTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onResolveTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onTakeTicket?(ticketId: number, action: WorkflowAction): Promise<void>;
  onViewTicket(ticketId: number): Promise<void>;
  tickets: TicketListItem[];
  title: string;
  user: CurrentUser;
}) {
  const [reassignTicket, setReassignTicket] = useState<TicketListItem | null>(null);

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h4 className="text-xl font-semibold tracking-tight">{title}</h4>
          <p className="mt-1 text-sm text-zinc-500">{tickets.length} tickets shown</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          <input
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
            placeholder="Search tickets"
            value={filters.search ?? ""}
            onChange={(event) => onChangeFilters({ ...filters, search: event.target.value })}
          />
          <select
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
            value={filters.status ?? ""}
            onChange={(event) => onChangeFilters({ ...filters, status: event.target.value })}
          >
            <option value="">All statuses</option>
            {["New", "Assigned", "In Progress", "Waiting for Requester", "Waiting Internal", "Resolved", "Reopened"].map(
              (status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ),
            )}
          </select>
          <select
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
            value={filters.priority ?? ""}
            onChange={(event) => onChangeFilters({ ...filters, priority: event.target.value })}
          >
            <option value="">All priorities</option>
            {["Low", "Medium", "High", "Critical"].map((priority) => (
              <option key={priority} value={priority}>
                {priority}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
            aria-label="Classification"
            value={filters.classification ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              onChangeFilters({
                ...filters,
                classification: (value || undefined) as TicketFilters["classification"],
              });
            }}
          >
            <option value="">All Classifications</option>
            <option value="FOC">Free of Charge</option>
            <option value="WARRANTY">Warranty</option>
            <option value="CHARGEABLE">Chargeable</option>
          </select>
        </div>
      </div>
      {isLoading ? <p className="mt-5 rounded-md bg-zinc-50 p-4 text-sm text-zinc-600">Loading tickets...</p> : null}
      <div className="mt-5 grid gap-3">
        {tickets.map((ticket) => (
          <StaffTicketCard
            actionMode={actionMode}
            key={ticket.id}
            onReassign={() => setReassignTicket(ticket)}
            onResolve={() =>
              onResolveTicket(ticket.id, {
                type: "resolve",
                resolutionComment: "Marked resolved from the Staff workflow dashboard.",
              })
            }
            onTake={() => onTakeTicket?.(ticket.id, { type: "take" })}
            onView={() => onViewTicket(ticket.id)}
            ticket={ticket}
          />
        ))}
      </div>
      {!isLoading && tickets.length === 0 ? (
        <p className="mt-5 rounded-lg border border-zinc-200 bg-zinc-50 p-8 text-center text-sm text-zinc-500">
          {emptyText}
        </p>
      ) : null}
      {reassignTicket ? (
        <ReassignTicketModal
          departmentUsers={departmentUsers}
          departments={departments}
          onClose={() => setReassignTicket(null)}
          onSubmit={async (payload) => {
            await onReassignTicket(reassignTicket.id, { type: "reassign", payload });
            setReassignTicket(null);
          }}
          ticket={reassignTicket}
          user={user}
        />
      ) : null}
    </section>
  );
}

function StaffTicketCard({
  actionMode,
  onReassign,
  onResolve,
  onTake,
  onView,
  ticket,
}: {
  actionMode: StaffTicketTab;
  onReassign(): void;
  onResolve(): void;
  onTake(): void;
  onView(): void;
  ticket: TicketListItem;
}) {
  const canMarkResolved = !isTerminalTicketStatus(ticket.status);

  return (
    <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h5 className="text-base font-semibold text-zinc-950">{ticket.title}</h5>
            <Badge className={statusBadgeClass(ticket.status)}>{ticket.status}</Badge>
            <Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge>
            {ticket.classification ? (
              <Badge className={classificationBadgeClass(ticket.classification)}>
                {classificationLabel(ticket.classification)}
              </Badge>
            ) : (
              <Badge className={classificationBadgeClass(null)}>Unclassified</Badge>
            )}
          </div>
          <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-500">
            <InlineMeta label="Ticket" value={ticket.ticket_no} />
            <InlineMeta label="Created" value={formatDate(ticket.created_at)} />
            <InlineMeta label="Group" value={ticket.owner_department_name ?? "-"} />
            <InlineMeta label="Requester" value={ticket.requester_name ?? "-"} />
            <InlineMeta label="Assignee" value={ticket.assignee_name ?? "Unassigned"} />
          </dl>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <StaffActionButton onClick={onView}>{actionMode === "submitted" ? "View Details" : "View"}</StaffActionButton>
          {actionMode === "available" ? <StaffActionButton onClick={onTake}>Take Ticket</StaffActionButton> : null}
          {actionMode === "assigned" ? (
            <>
              {canMarkResolved ? <StaffActionButton onClick={onResolve}>Mark Resolved</StaffActionButton> : null}
              <StaffActionButton onClick={onReassign}>Reassign</StaffActionButton>
            </>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function StaffActionButton({ children, onClick }: { children: string; onClick(): void }) {
  return (
    <button
      className="rounded-md border border-zinc-300 px-3 py-2 text-sm font-semibold text-zinc-800 transition hover:border-zinc-950 hover:bg-zinc-950 hover:text-white"
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}

function ReassignTicketModal({
  departmentUsers,
  departments,
  onClose,
  onSubmit,
  ticket,
  user,
}: {
  departmentUsers: ManagedUser[];
  departments: Department[];
  onClose(): void;
  onSubmit(payload: ReassignTicketPayload): Promise<void>;
  ticket: TicketListItem;
  user: CurrentUser;
}) {
  const [departmentId, setDepartmentId] = useState(ticket.owner_department_id ? String(ticket.owner_department_id) : "");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const availableDepartments = departments.length ? departments : [];

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    if (!departmentId) {
      setError("Department is required.");
      return;
    }
    if (!reason.trim()) {
      setError("Reason for reassignment is required.");
      return;
    }
    await onSubmit({ owner_department_id: Number(departmentId), assignee_id: null, reason: reason.trim() });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/45 px-4 py-6">
      <section className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <div>
          <h3 className="text-xl font-semibold">Reassign Ticket</h3>
          <p className="mt-1 text-sm text-zinc-500">
            {ticket.ticket_no} - {ticket.title}
          </p>
        </div>
        <dl className="mt-5 grid gap-3 rounded-md bg-zinc-50 p-4 text-sm sm:grid-cols-2">
          <DetailTerm label="Current Group" value={ticket.owner_department_name ?? "-"} />
          <DetailTerm label="Current Assignee" value={ticket.assignee_name ?? "Unassigned"} />
          <DetailTerm label="Requester" value={ticket.requester_name ?? "-"} />
          <DetailTerm label="Handled by" value={user.full_name} />
        </dl>
        <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-zinc-700">
            Department
            <select
              className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
              value={departmentId}
              onChange={(event) => setDepartmentId(event.target.value)}
            >
              <option value="">Select department</option>
              {availableDepartments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </label>
          {departmentUsers.length ? (
            <p className="text-xs text-zinc-500">
              {departmentUsers.length} active users are visible in your department for reference.
            </p>
          ) : null}
          <label className="block text-sm font-medium text-zinc-700">
            Reason for reassignment
            <textarea
              className="mt-2 min-h-28 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {error ? <p className="text-sm text-red-700">{error}</p> : null}
          <div className="flex justify-end gap-3">
            <button
              className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button className="rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700">
              Reassign Ticket
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function AdminDashboardPage({
  dashboard,
  departments,
  error,
  filters,
  isDashboardLoading,
  isTicketListLoading,
  onAssignTicket,
  onChangeFilters,
  onUnassignTicket,
  onViewTicket,
  tickets,
  user,
  users,
}: {
  dashboard: AdminDashboard | null;
  departments: Department[];
  error: string;
  filters: TicketFilters;
  isDashboardLoading: boolean;
  isTicketListLoading: boolean;
  onAssignTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onChangeFilters(filters: TicketFilters): void;
  onUnassignTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onViewTicket(ticketId: number): Promise<void>;
  tickets: TicketListItem[];
  user: CurrentUser;
  users: ManagedUser[];
}) {
  const visibleTickets = filterTicketsForStaff(tickets, filters);
  const inProgressCount = tickets.filter((ticket) => ticket.status === "In Progress").length;
  const cards = [
    { label: "Total Tickets", value: dashboard?.total ?? 0, tone: "bg-zinc-100 text-zinc-700", icon: "T" },
    { label: "Open", value: dashboard?.active ?? 0, tone: "bg-rose-50 text-rose-700", icon: "O" },
    { label: "In Progress", value: inProgressCount, tone: "bg-amber-50 text-amber-700", icon: "P" },
    { label: "Resolved", value: dashboard?.resolved ?? 0, tone: "bg-emerald-50 text-emerald-700", icon: "R" },
    { label: "Unassigned", value: dashboard?.unassigned ?? 0, tone: "bg-sky-50 text-sky-700", icon: "U" },
    { label: "High Priority", value: dashboard?.high_priority ?? 0, tone: "bg-red-50 text-red-700", icon: "H" },
  ];

  return (
    <section className="space-y-7">
      {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
      <section>
        <div>
          <h3 className="text-2xl font-semibold tracking-tight">Real-Time Ticket Tracking</h3>
          <p className="mt-1 text-sm text-zinc-500">Monitor system-wide support work and assignment coverage.</p>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {cards.map((card) => (
            <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" key={card.label}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-zinc-500">{card.label}</p>
                  <p className="mt-2 text-3xl font-bold text-zinc-950">{isDashboardLoading ? "-" : card.value}</p>
                </div>
                <span className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold ${card.tone}`}>
                  {card.icon}
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>
      <AdminTicketCardList
        departments={departments}
        filters={filters}
        isLoading={isTicketListLoading}
        onAssignTicket={onAssignTicket}
        onChangeFilters={onChangeFilters}
        onUnassignTicket={onUnassignTicket}
        onViewTicket={onViewTicket}
        tickets={visibleTickets}
        user={user}
        users={users}
      />
    </section>
  );
}

function AdminTicketCardList({
  departments,
  filters,
  isLoading,
  onAssignTicket,
  onChangeFilters,
  onUnassignTicket,
  onViewTicket,
  tickets,
  user,
  users,
}: {
  departments: Department[];
  filters: TicketFilters;
  isLoading: boolean;
  onAssignTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onChangeFilters(filters: TicketFilters): void;
  onUnassignTicket(ticketId: number, action: WorkflowAction): Promise<void>;
  onViewTicket(ticketId: number): Promise<void>;
  tickets: TicketListItem[];
  user: CurrentUser;
  users: ManagedUser[];
}) {
  const [assignmentTicket, setAssignmentTicket] = useState<TicketListItem | null>(null);

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h4 className="text-xl font-semibold tracking-tight">All Support Tickets</h4>
          <p className="mt-1 text-sm text-zinc-500">{tickets.length} tickets shown</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          <input
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
            placeholder="Search tickets"
            value={filters.search ?? ""}
            onChange={(event) => onChangeFilters({ ...filters, search: event.target.value })}
          />
          <select
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
            value={filters.status ?? ""}
            onChange={(event) => onChangeFilters({ ...filters, status: event.target.value })}
          >
            <option value="">All statuses</option>
            {["New", "Assigned", "In Progress", "Waiting for Requester", "Waiting Internal", "Resolved", "Closed", "Reopened"].map(
              (status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ),
            )}
          </select>
          <select
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
            value={filters.priority ?? ""}
            onChange={(event) => onChangeFilters({ ...filters, priority: event.target.value })}
          >
            <option value="">All priorities</option>
            {["Low", "Medium", "High", "Critical"].map((priority) => (
              <option key={priority} value={priority}>
                {priority}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
            aria-label="Classification"
            value={filters.classification ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              onChangeFilters({
                ...filters,
                classification: (value || undefined) as TicketFilters["classification"],
              });
            }}
          >
            <option value="">All Classifications</option>
            <option value="FOC">Free of Charge</option>
            <option value="WARRANTY">Warranty</option>
            <option value="CHARGEABLE">Chargeable</option>
          </select>
        </div>
      </div>
      {isLoading ? <p className="mt-5 rounded-md bg-zinc-50 p-4 text-sm text-zinc-600">Loading tickets...</p> : null}
      <div className="mt-5 grid gap-3">
        {tickets.map((ticket) => (
          <AdminTicketCard
            key={ticket.id}
            onAssign={() => setAssignmentTicket(ticket)}
            onUnassign={() => onUnassignTicket(ticket.id, { type: "unassign" })}
            onView={() => onViewTicket(ticket.id)}
            ticket={ticket}
          />
        ))}
      </div>
      {!isLoading && tickets.length === 0 ? (
        <p className="mt-5 rounded-lg border border-zinc-200 bg-zinc-50 p-8 text-center text-sm text-zinc-500">
          No support tickets match these filters.
        </p>
      ) : null}
      {assignmentTicket ? (
        <AdminAssignTicketModal
          departments={departments}
          onClose={() => setAssignmentTicket(null)}
          onSubmit={async (action) => {
            await onAssignTicket(assignmentTicket.id, action);
            setAssignmentTicket(null);
          }}
          ticket={assignmentTicket}
          user={user}
          users={users}
        />
      ) : null}
    </section>
  );
}

function AdminTicketCard({
  onAssign,
  onUnassign,
  onView,
  ticket,
}: {
  onAssign(): void;
  onUnassign(): void;
  onView(): void;
  ticket: TicketListItem;
}) {
  return (
    <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h5 className="text-base font-semibold text-zinc-950">{ticket.title}</h5>
            <Badge className={statusBadgeClass(ticket.status)}>{ticket.status}</Badge>
            <Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge>
            {ticket.classification ? (
              <Badge className={classificationBadgeClass(ticket.classification)}>
                {classificationLabel(ticket.classification)}
              </Badge>
            ) : null}
            {ticket.assignee_id === null ? <Badge className="bg-sky-50 text-sky-700">Unassigned</Badge> : null}
          </div>
          <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-500">
            <InlineMeta label="Ticket" value={ticket.ticket_no} />
            <InlineMeta label="Created" value={formatDate(ticket.created_at)} />
            <InlineMeta label="Group" value={ticket.owner_department_name ?? "-"} />
            <InlineMeta label="Requester" value={ticket.requester_name ?? "-"} />
            <InlineMeta label="Assignee" value={ticket.assignee_name ?? "Unassigned"} />
          </dl>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <StaffActionButton onClick={onView}>View</StaffActionButton>
          <StaffActionButton onClick={onAssign}>{ticket.assignee_id ? "Reassign" : "Assign"}</StaffActionButton>
          {ticket.assignee_id ? <StaffActionButton onClick={onUnassign}>Unassign</StaffActionButton> : null}
        </div>
      </div>
    </article>
  );
}

function AdminAssignTicketModal({
  departments,
  onClose,
  onSubmit,
  ticket,
  user,
  users,
}: {
  departments: Department[];
  onClose(): void;
  onSubmit(action: WorkflowAction): Promise<void>;
  ticket: TicketListItem;
  user: CurrentUser;
  users: ManagedUser[];
}) {
  const isReassign = ticket.assignee_id !== null;
  const [departmentId, setDepartmentId] = useState(ticket.owner_department_id ? String(ticket.owner_department_id) : "");
  const [assigneeId, setAssigneeId] = useState(ticket.assignee_id ? String(ticket.assignee_id) : "");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const assignableUsers = users.filter((managedUser) => managedUser.is_active && ["Staff", "Admin"].includes(managedUser.role));

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");

    const ownerDepartmentId = departmentId ? Number(departmentId) : null;
    const targetAssigneeId = assigneeId ? Number(assigneeId) : null;
    if (!ownerDepartmentId && !targetAssigneeId) {
      setError("Choose a department or staff assignee.");
      return;
    }
    if (isReassign && !reason.trim()) {
      setError("Reason is required for reassignment.");
      return;
    }

    if (isReassign) {
      await onSubmit({
        type: "reassign",
        payload: { owner_department_id: ownerDepartmentId, assignee_id: targetAssigneeId, reason: reason.trim() },
      });
      return;
    }

    await onSubmit({ type: "assign", payload: { owner_department_id: ownerDepartmentId, assignee_id: targetAssigneeId } });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/45 px-4 py-6">
      <section className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <div>
          <h3 className="text-xl font-semibold">{isReassign ? "Reassign Ticket" : "Assign Ticket"}</h3>
          <p className="mt-1 text-sm text-zinc-500">
            {ticket.ticket_no} - {ticket.title}
          </p>
        </div>
        <dl className="mt-5 grid gap-3 rounded-md bg-zinc-50 p-4 text-sm sm:grid-cols-2">
          <DetailTerm label="Current Group" value={ticket.owner_department_name ?? "-"} />
          <DetailTerm label="Current Assignee" value={ticket.assignee_name ?? "Unassigned"} />
          <DetailTerm label="Requester" value={ticket.requester_name ?? "-"} />
          <DetailTerm label="Handled by" value={user.full_name} />
        </dl>
        <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-zinc-700">
            Department
            <select
              className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
              value={departmentId}
              onChange={(event) => setDepartmentId(event.target.value)}
            >
              <option value="">No department change</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-zinc-700">
            Staff assignee
            <select
              className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
              value={assigneeId}
              onChange={(event) => setAssigneeId(event.target.value)}
            >
              <option value="">Leave unassigned</option>
              {assignableUsers.map((managedUser) => (
                <option key={managedUser.id} value={managedUser.id}>
                  {managedUser.full_name} ({managedUser.role})
                </option>
              ))}
            </select>
          </label>
          {isReassign ? (
            <label className="block text-sm font-medium text-zinc-700">
              Reason / notes
              <textarea
                className="mt-2 min-h-28 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
          ) : null}
          {error ? <p className="text-sm text-red-700">{error}</p> : null}
          <div className="flex justify-end gap-3">
            <button
              className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button className="rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700">
              {isReassign ? "Reassign Ticket" : "Assign Ticket"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function DashboardPage({
  data,
  error,
  isLoading,
  onOpenTicketList,
  page,
}: {
  data: DashboardData | null;
  error: string;
  isLoading: boolean;
  onOpenTicketList(page: PageKey, filters?: TicketFilters): void;
  page: PageKey;
}) {
  const cards = dashboardCards(page, data);

  if (isLoading) {
    return <p className="mt-8 rounded-md bg-white p-6 text-sm text-zinc-600">Loading dashboard...</p>;
  }

  if (error) {
    return <p className="mt-8 rounded-md bg-red-50 p-6 text-sm text-red-800">{error}</p>;
  }

  return (
    <section className="mt-8">
      <div className="grid gap-4 md:grid-cols-3">
        {cards.map((card) => {
          const content = (
            <>
              <p className="text-sm font-medium text-zinc-500">{card.label}</p>
              <p className="mt-3 text-3xl font-semibold text-zinc-950">{card.value}</p>
            </>
          );
          if (!card.page) {
            return (
              <div className="rounded-lg border border-zinc-200 bg-white p-5" key={card.label}>
                {content}
              </div>
            );
          }
          return (
            <button
              className="rounded-lg border border-zinc-200 bg-white p-5 text-left transition hover:border-sky-300 hover:bg-sky-50"
              key={card.label}
              onClick={() => onOpenTicketList(card.page!, card.filters ?? {})}
              type="button"
            >
              {content}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function dashboardCards(page: PageKey, data: DashboardData | null): DashboardCard[] {
  if (page === "requester-dashboard") {
    const dashboard = data as RequesterDashboard | null;
    return [
      { label: "Total", value: dashboard?.total ?? 0, page: "my-tickets" },
      { label: "Open / Active", value: dashboard?.active ?? 0, page: "my-tickets" },
      {
        label: "Waiting for Me",
        value: dashboard?.waiting_for_me ?? 0,
        page: "my-tickets",
        filters: { status: "Waiting for Requester" },
      },
      { label: "Resolved", value: dashboard?.resolved ?? 0, page: "my-tickets", filters: { status: "Resolved" } },
      { label: "Closed", value: dashboard?.closed ?? 0, page: "my-tickets", filters: { status: "Closed" } },
      { label: "Reopened", value: dashboard?.reopened ?? 0, page: "my-tickets", filters: { status: "Reopened" } },
      { label: "Overdue", value: dashboard?.overdue ?? 0, page: "my-tickets", filters: { overdue_only: "true" } },
    ];
  }

  if (page === "staff-dashboard") {
    const dashboard = data as StaffDashboard | null;
    return [
      { label: "My Assigned", value: dashboard?.my_assigned ?? 0, page: "my-assigned-tickets" },
      {
        label: "In Progress",
        value: dashboard?.in_progress ?? 0,
        page: "my-assigned-tickets",
        filters: { status: "In Progress" },
      },
      { label: "Waiting", value: dashboard?.waiting ?? 0, page: "my-assigned-tickets" },
      { label: "Resolved", value: dashboard?.resolved ?? 0, page: "my-assigned-tickets", filters: { status: "Resolved" } },
      { label: "Reopened", value: dashboard?.reopened ?? 0, page: "my-assigned-tickets", filters: { status: "Reopened" } },
      { label: "Overdue", value: dashboard?.overdue ?? 0, page: "my-assigned-tickets", filters: { overdue_only: "true" } },
      {
        label: "Available Tickets",
        value: dashboard?.available_unassigned_in_my_department ?? 0,
        page: "available-tickets",
      },
    ];
  }

  const dashboard = data as AdminDashboard | null;
  return [
    { label: "Total", value: dashboard?.total ?? 0, page: "all-tickets" },
    { label: "Unassigned", value: dashboard?.unassigned ?? 0, page: "all-tickets" },
    { label: "Active", value: dashboard?.active ?? 0, page: "all-tickets" },
    { label: "Waiting", value: dashboard?.waiting ?? 0, page: "all-tickets" },
    { label: "Resolved", value: dashboard?.resolved ?? 0, page: "all-tickets", filters: { status: "Resolved" } },
    { label: "Closed", value: dashboard?.closed ?? 0, page: "all-tickets", filters: { status: "Closed" } },
    { label: "Reopened", value: dashboard?.reopened ?? 0, page: "all-tickets", filters: { status: "Reopened" } },
    { label: "Overdue", value: dashboard?.overdue ?? 0, page: "all-tickets", filters: { overdue_only: "true" } },
    { label: "High Priority", value: dashboard?.high_priority ?? 0 },
  ];
}

function CreateTicketPage({
  error,
  filesRevision,
  isCustomer,
  isRequester,
  message,
  onCancel,
  onCreateTicket,
  requestTypes,
  submitPhase,
  submitting,
  uploadProgress,
}: {
  error: string;
  filesRevision: number;
  isCustomer: boolean;
  isRequester: boolean;
  message: string;
  onCancel(): void;
  onCreateTicket(payload: TicketCreatePayload, uploadSnapshot: File[]): Promise<{ ok: boolean; staleCategory: boolean }>;
  requestTypes: RequestType[];
  submitPhase: CreateSubmitPhase | null;
  submitting: boolean;
  uploadProgress: CreateUploadProgress | null;
}) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    request_type_id: "",
    priority: "Medium" as TicketCreatePayload["priority"],
    target_date: defaultTargetDate(),
  });
  const [formError, setFormError] = useState("");
  const [selectedAttachments, setSelectedAttachments] = useState<SelectedAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastFilesRevisionRef = useRef<number>(filesRevision);

  const selectedRequestType = form.request_type_id
    ? requestTypes.find((requestType) => String(requestType.id) === form.request_type_id) ?? null
    : null;
  useEffect(() => {
    if (lastFilesRevisionRef.current === filesRevision) {
      return;
    }
    lastFilesRevisionRef.current = filesRevision;
    setSelectedAttachments([]);
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = "";
    }
  }, [filesRevision]);

  function addFiles(rawFiles: FileList | File[]) {
    const incoming = Array.from(rawFiles);
    if (incoming.length === 0) {
      return;
    }
    setSelectedAttachments((current) => {
      const existingKeys = new Set(current.map((entry) => attachmentMetadataKey(entry.file)));
      const additions: SelectedAttachment[] = [];
      for (const file of incoming) {
        const key = attachmentMetadataKey(file);
        if (existingKeys.has(key)) {
          continue;
        }
        existingKeys.add(key);
        additions.push({
          id: buildCreateAttachmentId(),
          file,
          name: file.name,
          size: file.size,
          lastModified: file.lastModified,
          type: file.type,
        });
      }
      return [...current, ...additions];
    });
  }

  function handleFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (files !== null && files.length > 0) {
      addFiles(files);
    }
    event.target.value = "";
  }

  function handleRemoveAttachment(id: string) {
    setSelectedAttachments((current) => current.filter((entry) => entry.id !== id));
  }

  function handleClearAttachments() {
    setSelectedAttachments([]);
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = "";
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) {
      return;
    }
    setFormError("");

    if (
      !form.title.trim() ||
      !form.description.trim() ||
      !form.request_type_id ||
      !form.priority ||
      !form.target_date
    ) {
      setFormError("All ticket fields are required.");
      return;
    }

    const snapshot = selectedAttachments.map((entry) => entry.file);
    const result = await onCreateTicket(
      {
        title: form.title,
        description: form.description,
        request_type_id: Number(form.request_type_id),
        priority: form.priority,
        target_date: form.target_date,
      },
      snapshot,
    );
    if (result.staleCategory) {
      setForm((current) => (current.request_type_id === "" ? current : { ...current, request_type_id: "" }));
      setFormError("");
    }
    if (!result.ok) {
      return;
    }

    setForm({
      title: "",
      description: "",
      request_type_id: "",
      priority: "Medium",
      target_date: defaultTargetDate(),
    });
  }

  return (
    <section className="space-y-5">
      {isCustomer ? (
        <div>
          <h3 className="text-2xl font-semibold tracking-tight">Create a Ticket</h3>
          <p className="mt-1 text-sm text-zinc-500">Tell us what you need and the right support group will follow up.</p>
        </div>
      ) : null}
      {message ? <p className="mb-4 rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</p> : null}
      {error ? <p className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
      <form
        className={`rounded-lg border border-zinc-200 bg-white p-6 shadow-sm ${
          isCustomer ? "grid gap-5 lg:grid-cols-3" : "grid gap-4 md:grid-cols-2"
        }`}
        onSubmit={handleSubmit}
      >
        {isCustomer ? (
          <div className="lg:col-span-3">
            <h4 className="text-lg font-semibold">Submit Support Ticket</h4>
            <p className="mt-1 text-sm text-zinc-500">
              Complete the form below with enough detail for the support team to triage your request.
            </p>
          </div>
        ) : null}
        <TextInput
          className={isCustomer ? "lg:col-span-3" : ""}
          label="Title"
          value={form.title}
          onChange={(value) => setForm((current) => ({ ...current, title: value }))}
        />
        <div className="block text-sm font-medium text-zinc-700">
          <label htmlFor="create-ticket-priority">
            Priority
          </label>
          <select
            id="create-ticket-priority"
            aria-describedby="create-ticket-priority-helper"
            className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
            value={form.priority}
            onChange={(event) =>
              setForm((current) => ({ ...current, priority: event.target.value as TicketCreatePayload["priority"] }))
            }
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </select>
          <p id="create-ticket-priority-helper" className="mt-2 text-xs font-normal text-zinc-500">
            Choose how urgent the request feels today. Low for minor follow-ups, Medium for normal work, High when something is blocking you, and Critical only for severe incidents. We do not promise specific response times.
          </p>
        </div>
        <div className="block text-sm font-medium text-zinc-700">
          <label htmlFor="create-ticket-request-type">
            Request Category
          </label>
          <select
            id="create-ticket-request-type"
            aria-describedby="create-ticket-request-type-helper"
            className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
            value={form.request_type_id}
            onChange={(event) => setForm((current) => ({ ...current, request_type_id: event.target.value }))}
          >
            <option value="">Select request category</option>
            {requestTypes.map((requestType) => (
              <option key={requestType.id} value={requestType.id}>
                {requestType.name}
              </option>
            ))}
          </select>
          <p id="create-ticket-request-type-helper" className="mt-2 text-xs font-normal text-zinc-500">
            Your request will be routed automatically based on the selected category.
          </p>
          {selectedRequestType && selectedRequestType.description ? (
            <p className="mt-2 text-xs font-normal text-zinc-500" aria-live="polite">
              {selectedRequestType.description}
            </p>
          ) : null}
          {requestTypes.length === 0 ? (
            <p className="mt-2 text-xs font-normal text-zinc-500" aria-live="polite">
              No Request Categories are currently available for your account. Please contact support.
            </p>
          ) : null}
        </div>
        {!isCustomer ? (
          <TextInput
            label="Target Date"
            type="date"
            value={form.target_date}
            onChange={(value) => setForm((current) => ({ ...current, target_date: value }))}
          />
        ) : null}
        <label className={`block text-sm font-medium text-zinc-700 ${isCustomer ? "lg:col-span-3" : "md:col-span-2"}`}>
          Description
          <textarea
            className="mt-2 min-h-36 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
          />
        </label>
        {isCustomer ? (
          isRequester ? (
            <div
              aria-label="Attachments"
              className="rounded-md border border-dashed border-zinc-300 bg-zinc-50 p-4 lg:col-span-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-zinc-700">Attachments (Optional)</p>
                {selectedAttachments.length > 0 ? (
                  <button
                    className="rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={submitting}
                    onClick={handleClearAttachments}
                    type="button"
                  >
                    Clear all
                  </button>
                ) : null}
              </div>
              <p className="mt-1 text-sm text-zinc-500">
                Files are validated securely when uploaded.
              </p>
              <div className="mt-3">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold text-zinc-800 hover:bg-zinc-100">
                  <input
                    aria-label="Add attachments"
                    className="hidden"
                    disabled={submitting}
                    multiple
                    onChange={(event) => {
                      handleFileInputChange(event);
                    }}
                    ref={fileInputRef}
                    type="file"
                  />
                  Add files
                </label>
              </div>
              {selectedAttachments.length > 0 ? (
                <ul className="mt-3 space-y-2">
                  {selectedAttachments.map((entry) => (
                    <li
                      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-zinc-200 bg-white p-3"
                      key={entry.id}
                    >
                      <div className="min-w-0">
                        <p className="break-all text-sm font-semibold text-zinc-900">
                          {entry.name}
                        </p>
                        <p className="mt-0.5 text-xs text-zinc-500">
                          {formatFileSize(entry.size)}
                        </p>
                      </div>
                      <button
                        className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={submitting}
                        onClick={() => handleRemoveAttachment(entry.id)}
                        type="button"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {submitPhase !== null ? (
                <p className="mt-3 text-sm text-zinc-600">
                  {submitPhase === "creating"
                    ? "Creating ticket..."
                    : uploadProgress !== null
                    ? `Uploading attachment ${uploadProgress.current} of ${uploadProgress.total}: ${uploadProgress.name}`
                    : "Uploading attachments..."}
                </p>
              ) : null}
            </div>
          ) : (
            <div
              aria-label="Attachments placeholder"
              className="rounded-md border border-dashed border-zinc-300 bg-zinc-50 p-4 lg:col-span-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-zinc-700">Attachments (Optional)</p>
                <span className="rounded-full bg-zinc-200 px-2.5 py-0.5 text-xs font-semibold text-zinc-600">
                  Coming soon
                </span>
              </div>
              <p className="mt-1 text-sm text-zinc-500">
                Files are validated securely when uploaded.
              </p>
            </div>
          )
        ) : null}
        {formError ? <p className={`${isCustomer ? "lg:col-span-3" : "md:col-span-2"} text-sm text-red-700`}>{formError}</p> : null}
        <div className={`flex justify-end gap-3 ${isCustomer ? "lg:col-span-3" : "md:col-span-2"}`}>
          <button
            className="rounded-md border border-zinc-300 px-5 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="rounded-md bg-rose-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={submitting}
            type="submit"
          >
            {submitPhase === "creating"
              ? "Creating ticket..."
              : submitPhase === "uploading"
              ? "Uploading..."
              : "Submit Ticket"}
          </button>
        </div>
      </form>
    </section>
  );
}

function TicketListPage({
  departments,
  error,
  filters,
  isLoading,
  onChangeFilters,
  onViewTicket,
  requestTypes,
  tickets,
  title,
  user,
}: {
  departments: Department[];
  error: string;
  filters: TicketFilters;
  isLoading: boolean;
  onChangeFilters(filters: TicketFilters): void;
  onViewTicket(ticketId: number): Promise<void>;
  requestTypes: RequestType[];
  tickets: TicketListItem[];
  title: string;
  user: CurrentUser;
}) {
  function updateFilter(key: keyof TicketFilters, value: string) {
    onChangeFilters({ ...filters, [key]: value });
  }

  return (
    <section className="mt-8 rounded-lg border border-zinc-200 bg-white p-6">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-zinc-500">{tickets.length} tickets</p>
      </div>
      {error ? <p className="mt-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
      <div className="mt-5 grid gap-3 md:grid-cols-6">
        <input
          className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-sky-600"
          placeholder="Search ticket or title"
          value={filters.search ?? ""}
          onChange={(event) => updateFilter("search", event.target.value)}
        />
        <select
          className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-sky-600"
          value={filters.status ?? ""}
          onChange={(event) => updateFilter("status", event.target.value)}
        >
          <option value="">All statuses</option>
          {["New", "Assigned", "In Progress", "Waiting for Requester", "Waiting Internal", "Resolved", "Closed", "Reopened"].map(
            (status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ),
          )}
        </select>
        <select
          className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-sky-600"
          value={filters.priority ?? ""}
          onChange={(event) => updateFilter("priority", event.target.value)}
        >
          <option value="">All priorities</option>
          {["Low", "Medium", "High", "Critical"].map((priority) => (
            <option key={priority} value={priority}>
              {priority}
            </option>
          ))}
        </select>
        {user.role === "Admin" ? (
          <select
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-sky-600"
            aria-label="Classification"
            value={filters.classification ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              onChangeFilters({
                ...filters,
                classification: (value || undefined) as TicketFilters["classification"],
              });
            }}
          >
            <option value="">All Classifications</option>
            <option value="FOC">Free of Charge</option>
            <option value="WARRANTY">Warranty</option>
            <option value="CHARGEABLE">Chargeable</option>
          </select>
        ) : null}
        <select
          className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-sky-600"
          value={filters.request_type_id ?? ""}
          onChange={(event) => updateFilter("request_type_id", event.target.value)}
        >
          <option value="">All request types</option>
          {requestTypes.map((requestType) => (
            <option key={requestType.id} value={requestType.id}>
              {requestType.name}
            </option>
          ))}
        </select>
        <select
          className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-sky-600"
          value={filters.owner_department_id ?? ""}
          onChange={(event) => updateFilter("owner_department_id", event.target.value)}
        >
          <option value="">All departments</option>
          {departments.map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </select>
      </div>
      {isLoading ? <p className="mt-5 rounded-md bg-zinc-50 p-4 text-sm text-zinc-600">Loading tickets...</p> : null}
      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[1000px] border-collapse text-left text-sm">
          <thead className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="py-3 pr-4">Ticket</th>
              <th className="py-3 pr-4">Title</th>
              <th className="py-3 pr-4">Status</th>
              <th className="py-3 pr-4">Priority</th>
              <th className="py-3 pr-4">Request Type</th>
              <th className="py-3 pr-4">Department</th>
              <th className="py-3 pr-4">Requester</th>
              <th className="py-3 pr-4">Assignee</th>
              <th className="py-3 pr-4">Target Date</th>
              <th className="py-3 pr-4">Age</th>
              <th className="py-3 pr-4">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {tickets.map((ticket) => (
              <tr key={ticket.id}>
                <td className="py-3 pr-4">
                  <button className="font-semibold text-sky-700 hover:text-sky-900" onClick={() => onViewTicket(ticket.id)}>
                    {ticket.ticket_no}
                  </button>
                </td>
                <td className="py-3 pr-4 text-zinc-950">{ticket.title}</td>
                <td className="py-3 pr-4">
                  <Badge className={statusBadgeClass(ticket.status)}>{ticket.status}</Badge>
                </td>
                <td className="py-3 pr-4">
                  <Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge>
                </td>
                <td className="py-3 pr-4 text-zinc-600">{ticket.request_type_name ?? "-"}</td>
                <td className="py-3 pr-4 text-zinc-600">{ticket.owner_department_name ?? "-"}</td>
                <td className="py-3 pr-4 text-zinc-600">{ticket.requester_name ?? "-"}</td>
                <td className="py-3 pr-4 text-zinc-600">{ticket.assignee_name ?? "-"}</td>
                <td className="py-3 pr-4 text-zinc-600">
                  <div className="flex flex-col gap-1">
                    <span>{formatDate(ticket.target_date)}</span>
                    {ticket.overdue ? <Badge className="bg-red-50 text-red-700">Overdue</Badge> : null}
                  </div>
                </td>
                <td className="py-3 pr-4 text-zinc-600">{ticket.aging_days}d</td>
                <td className="py-3 pr-4 text-zinc-600">
                  {formatDate(ticket.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && tickets.length === 0 ? (
          <p className="border-t border-zinc-100 py-8 text-center text-sm text-zinc-500">
            No tickets match the current filters.
          </p>
        ) : null}
      </div>
    </section>
  );
}

function formatFileSize(value: number) {
  if (!Number.isFinite(value) || value <= 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB"];
  let amount = value;
  let unitIndex = 0;
  while (amount >= 1024 && unitIndex < units.length - 1) {
    amount /= 1024;
    unitIndex += 1;
  }
  if (unitIndex === 0) {
    return `${Math.round(amount)} ${units[unitIndex]}`;
  }
  return `${amount.toFixed(amount >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

function attachmentErrorMessage(error: unknown): string {
  if (error instanceof AttachmentApiError) {
    return error.message;
  }
  return "Unable to complete the attachment operation. Please try again.";
}

function TicketAttachmentsSection({
  ticket,
  user,
  onActivityChanged,
}: {
  ticket: TicketDetail;
  user: CurrentUser;
  onActivityChanged?: (ticketId: number) => void | Promise<void>;
}) {
  const [items, setItems] = useState<TicketAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploadNotice, setUploadNotice] = useState("");
  const [uploadingName, setUploadingName] = useState("");
  const [attachmentIsInternal, setAttachmentIsInternal] = useState(
    user.role === "Staff" || user.role === "Admin",
  );
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const ticketIdRef = useRef<number>(ticket.id);

  useEffect(() => {
    let cancelled = false;
    listTicketAttachments(ticket.id)
      .then((rows) => {
        if (cancelled) {
          return;
        }
        setItems(rows);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        setLoadError(attachmentErrorMessage(error));
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ticket.id]);

  async function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }
    const currentTicketId = ticket.id;
    if (ticketIdRef.current !== currentTicketId) {
      event.target.value = "";
      return;
    }
    setUploadError("");
    setUploadNotice("");
    let successCount = 0;
    let failureCount = 0;
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      if (ticketIdRef.current !== currentTicketId) {
        break;
      }
      setUploadingName(file.name);
      try {
        const created = await uploadTicketAttachment(
          currentTicketId,
          file,
          attachmentIsInternal,
        );
        if (ticketIdRef.current !== currentTicketId) {
          break;
        }
        setItems((current) => {
          if (current.some((entry) => entry.id === created.id)) {
            return current;
          }
          return [...current, created];
        });
        successCount += 1;
        if (typeof onActivityChanged === 'function') {
          try {
            await onActivityChanged(currentTicketId);
          } catch {
            // Timeline refresh is best-effort; upload already succeeded.
          }
        }
      } catch (error: unknown) {
        if (ticketIdRef.current !== currentTicketId) {
          break;
        }
        failureCount += 1;
        setUploadError(attachmentErrorMessage(error));
      }
    }
    if (ticketIdRef.current === currentTicketId) {
      setUploadingName("");
      if (successCount > 0 && failureCount === 0) {
        setUploadNotice(
          successCount === 1
            ? "Attachment uploaded."
            : `${successCount} attachments uploaded.`,
        );
      } else if (successCount > 0 && failureCount > 0) {
        setUploadNotice(
          `${successCount} uploaded, ${failureCount} failed. ${uploadError || ""}`.trim(),
        );
      } else if (failureCount > 0) {
        setUploadNotice("");
      }
    }
    event.target.value = "";
  }

  async function handleDownload(attachment: TicketAttachment) {
    const currentTicketId = ticket.id;
    if (downloadingId !== null) {
      return;
    }
    setDownloadingId(attachment.id);
    let objectUrl: string | null = null;
    try {
      const result = await downloadTicketAttachment(
        currentTicketId,
        attachment.id,
        attachment.original_filename,
      );
      if (ticketIdRef.current !== currentTicketId) {
        return;
      }
      objectUrl = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = result.filename;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (error: unknown) {
      if (ticketIdRef.current === currentTicketId) {
        setUploadError(attachmentErrorMessage(error));
      }
    } finally {
      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl);
      }
      if (ticketIdRef.current === currentTicketId) {
        setDownloadingId(null);
      }
    }
  }

  async function handleDelete(attachment: TicketAttachment) {
    const currentTicketId = ticket.id;
    if (!canDeleteFor(attachment)) {
      return;
    }
    if (deletingId !== null) {
      return;
    }
    if (typeof window !== "undefined" && typeof window.confirm === "function") {
      const confirmed = window.confirm(
        `Delete attachment "${attachment.original_filename}"? This action cannot be undone.`,
      );
      if (!confirmed) {
        return;
      }
    }
    setDeletingId(attachment.id);
    setUploadError("");
    try {
      await deleteTicketAttachment(currentTicketId, attachment.id);
      if (ticketIdRef.current === currentTicketId) {
        setItems((current) => current.filter((entry) => entry.id !== attachment.id));
        setUploadNotice("Attachment deleted.");
        if (typeof onActivityChanged === 'function') {
          try {
            await onActivityChanged(currentTicketId);
          } catch {
            // Timeline refresh is best-effort; deletion already succeeded.
          }
        }
      }
    } catch (error: unknown) {
      if (ticketIdRef.current !== currentTicketId) {
        return;
      }
      if (error instanceof AttachmentApiError && error.status === 410) {
        setItems((current) => current.filter((entry) => entry.id !== attachment.id));
        setUploadError("This attachment is no longer available.");
        return;
      }
      if (error instanceof AttachmentApiError && error.status === 404) {
        setItems((current) => current.filter((entry) => entry.id !== attachment.id));
        setUploadError("The attachment could not be found.");
        return;
      }
      if (error instanceof AttachmentApiError && error.status === 403) {
        setUploadError("You are not allowed to delete this attachment.");
        return;
      }
      setUploadError(attachmentErrorMessage(error));
    } finally {
      if (ticketIdRef.current === currentTicketId) {
        setDeletingId(null);
      }
    }
  }

  const canUploadFor = (
    targetTicket: TicketDetail,
    targetUser: CurrentUser,
  ): boolean => {
    if (targetUser.role === "Admin") {
      return true;
    }
    if (targetUser.role === "Staff") {
      if (targetTicket.assignee_id === targetUser.id) {
        return true;
      }
      if (targetTicket.requester_id === targetUser.id) {
        return true;
      }
      if (
        targetUser.department_id !== null &&
        targetTicket.owner_department_id === targetUser.department_id &&
        targetTicket.assignee_id === null
      ) {
        return true;
      }
      return false;
    }
    return targetTicket.requester_id === targetUser.id;
  };

  const canDeleteFor = (attachment: TicketAttachment) =>
    user.role === "Admin" ||
    (attachment.uploaded_by === user.id &&
      ticket.status !== "Closed" &&
      ticket.status !== "Resolved");
  const canSetInternalVisibility = user.role === "Staff" || user.role === "Admin";

  return (
    <div className="mt-5 rounded-md border border-dashed border-zinc-300 bg-zinc-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className="text-sm font-semibold text-zinc-700">Attachments</h5>
        <span className="text-xs text-zinc-500">
          Files are validated securely when uploaded.
        </span>
      </div>
      {uploadNotice ? (
        <p className="mt-2 text-sm text-emerald-700">{uploadNotice}</p>
      ) : null}
      {uploadError ? (
        <p className="mt-2 text-sm text-red-700">{uploadError}</p>
      ) : null}
      {loadError ? (
        <p className="mt-2 text-sm text-red-700">{loadError}</p>
      ) : null}
      {canUploadFor(ticket, user) ? (
        <div className="mt-3 space-y-3">
          {canSetInternalVisibility ? (
            <label className="block max-w-xs text-sm font-medium text-zinc-700">
              Attachment visibility
              <select
                aria-label="Attachment visibility"
                className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                disabled={uploadingName !== ""}
                onChange={(event) => setAttachmentIsInternal(event.target.value === "internal")}
                value={attachmentIsInternal ? "internal" : "customer"}
              >
                <option value="customer">Customer-visible</option>
                <option value="internal">Internal</option>
              </select>
            </label>
          ) : null}
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-semibold text-zinc-800 hover:bg-zinc-100">
            <input
              aria-label="Upload attachment"
              className="hidden"
              disabled={uploadingName !== ""}
              multiple
              onChange={(event) => {
                void handleUpload(event);
              }}
              type="file"
            />
            {uploadingName ? `Uploading ${uploadingName}...` : "Add files"}
          </label>
        </div>
      ) : null}
      {isLoading ? (
        <p className="mt-3 text-sm text-zinc-500">Loading attachments...</p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-sm text-zinc-500">No attachments</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((attachment) => (
            <li
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-zinc-200 bg-white p-3"
              key={attachment.id}
            >
              <div className="min-w-0">
                <p className="break-all text-sm font-semibold text-zinc-900">
                  {attachment.original_filename}
                </p>
                <p className="mt-0.5 text-xs text-zinc-500">
                  {formatFileSize(attachment.size_bytes)} ·
                  {" "}
                  {attachment.uploader_name ?? "Unknown"} ·
                  {" "}
                  {formatDateTime(attachment.created_at)}
                  {attachment.is_internal ? (
                    <>
                      {" "}· <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">Internal</span>
                    </>
                  ) : null}
                  {attachment.status && attachment.status !== "ok" ? (
                    <>
                      {" "}· <span className="text-amber-700">File unavailable</span>
                    </>
                  ) : null}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm font-semibold text-zinc-800 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={downloadingId === attachment.id}
                  onClick={() => {
                    void handleDownload(attachment);
                  }}
                  type="button"
                >
                  {downloadingId === attachment.id ? "Downloading..." : "Download"}
                </button>
                {canDeleteFor(attachment) ? (
                  <button
                    className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={deletingId === attachment.id}
                    onClick={() => {
                      void handleDelete(attachment);
                    }}
                    type="button"
                  >
                    {deletingId === attachment.id ? "Deleting..." : "Delete"}
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TicketDetailPage({
  activity,
  comments,
  createNotice,
  departments,
  error,
  message,
  onAddComment,
  onBack,
  onRefreshTimeline,
  onRespondToChargeableRequest,
  onWorkflowAction,
  ticket,
  user,
  users,
}: {
  activity: TicketActivity[];
  comments: TicketComment[];
  createNotice: CreateTicketNotice | null;
  departments: Department[];
  error: string;
  message: string;
  onAddComment(ticketId: number, commentText: string, isInternal?: boolean): Promise<void>;
  onBack(): void;
  onRefreshTimeline(ticketId: number): Promise<void>;
  onRespondToChargeableRequest(ticketId: number, payload: ChargeableResponsePayload): Promise<WorkflowActionResponse>;
  onWorkflowAction(ticketId: number, action: WorkflowAction): Promise<void>;
  ticket: TicketDetail;
  user: CurrentUser;
  users: ManagedUser[];
}) {
  const [commentText, setCommentText] = useState("");
  const [commentIsInternal, setCommentIsInternal] = useState(user.role !== "Requester");
  const [commentError, setCommentError] = useState("");
  const [workflowForm, setWorkflowForm] = useState<{
    action: WorkflowAction["type"] | "classify" | "";
    ownerDepartmentId: string;
    assigneeId: string;
    reason: string;
    resolutionComment: string;
    classification: string;
    assessmentNote: string;
    chargeableExplanation: string;
  }>({
    action: "",
    ownerDepartmentId: ticket.owner_department_id ? String(ticket.owner_department_id) : "",
    assigneeId: ticket.assignee_id ? String(ticket.assignee_id) : "",
    reason: "",
    resolutionComment: "",
    classification: "",
    assessmentNote: "",
    chargeableExplanation: "",
  });
  const [workflowError, setWorkflowError] = useState("");
  const [classifySaving, setClassifySaving] = useState(false);
  const [chargeableSaving, setChargeableSaving] = useState(false);
  const [staffReassignTicket, setStaffReassignTicket] = useState<TicketDetail | null>(null);
  const [adminAssignmentTicket, setAdminAssignmentTicket] = useState<TicketDetail | null>(null);

  const isAdmin = user.role === "Admin";
  const isStaff = user.role === "Staff";
  const isRequester = user.role === "Requester";
  const isAssignedStaff = isStaff && ticket.assignee_id === user.id;
  const ownsTicket = ticket.requester_id === user.id;

  // C4: Requester chargeable response form state
  const [chargeableResponseOption, setChargeableResponseOption] = useState<"proceed" | "decline" | "clarify" | "">("");
  const [chargeableClarificationText, setChargeableClarificationText] = useState("");
  const [chargeableResponseSaving, setChargeableResponseSaving] = useState(false);
  const [chargeableResponseError, setChargeableResponseError] = useState("");

  const showChargeableResponseUI =
    isRequester &&
    ownsTicket &&
    ticket.status === "Waiting for Requester" &&
    ticket.chargeable_customer_state === "awaiting_your_decision" &&
    ticket.chargeable_decision_required === true &&
    ticket.chargeable_request_generation !== null;
  const canTake =
    isStaff &&
    ticket.assignee_id === null &&
    ticket.owner_department_id !== null &&
    ticket.owner_department_id === user.department_id;
  const staffCanAct = isAdmin || isAssignedStaff;
  const canClassify = isAdmin ? true : isAssignedStaff && ticket.status !== "Closed";
  const canIssueChargeableRequest =
    staffCanAct &&
    ticket.classification === "CHARGEABLE" &&
    ticket.status !== "Closed";
  const canStartProgress =
    staffCanAct &&
    ["Assigned", "Reopened"].includes(ticket.status) &&
    ticket.classification !== "CHARGEABLE";
  const activeAction = workflowForm.action;

  async function handleSubmitChargeableResponse(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (chargeableResponseSaving) {
      return;
    }
    setChargeableResponseError("");

    if (chargeableResponseOption === "clarify") {
      const trimmed = chargeableClarificationText.trim();
      if (!trimmed) {
        setChargeableResponseError("Clarification text is required.");
        return;
      }
    }

    const generation = ticket.chargeable_request_generation as number;
    const payload: ChargeableResponsePayload = {
      generation,
      response: chargeableResponseOption as "proceed" | "decline" | "clarify",
    };
    if (chargeableResponseOption === "clarify") {
      payload.clarification_text = chargeableClarificationText.trim();
    }

    setChargeableResponseSaving(true);
    try {
      await onRespondToChargeableRequest(ticket.id, payload);
      setChargeableResponseOption("");
      setChargeableClarificationText("");
      setChargeableResponseError("");
    } catch (caught: unknown) {
      if (caught instanceof TicketApiError && caught.status === 409) {
        setChargeableResponseError(
          "The chargeable request has been updated or is no longer pending. Reload the ticket before responding.",
        );
      } else {
        const detail =
          caught instanceof TicketApiError ? caught.detail : "Unable to submit chargeable response.";
        setChargeableResponseError(detail);
      }
    } finally {
      setChargeableResponseSaving(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCommentError("");
    if (!commentText.trim()) {
      setCommentError("Comment text is required.");
      return;
    }
    await onAddComment(ticket.id, commentText, !isRequester && commentIsInternal);
    setCommentText("");
    setCommentIsInternal(!isRequester);
  }

  function openWorkflowForm(action: WorkflowAction["type"] | "classify") {
    setWorkflowError("");
    setWorkflowForm({
      action,
      ownerDepartmentId: ticket.owner_department_id ? String(ticket.owner_department_id) : "",
      assigneeId: ticket.assignee_id ? String(ticket.assignee_id) : "",
      reason: "",
      resolutionComment: "",
      classification: "",
      assessmentNote: "",
      chargeableExplanation: "",
    });
  }

  async function submitWorkflowForm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setWorkflowError("");

    const ownerDepartmentId = workflowForm.ownerDepartmentId ? Number(workflowForm.ownerDepartmentId) : null;
    const assigneeId = workflowForm.assigneeId ? Number(workflowForm.assigneeId) : null;
    const reason = workflowForm.reason.trim();
    const resolutionComment = workflowForm.resolutionComment.trim();

    if ((activeAction === "assign" || activeAction === "reassign") && !ownerDepartmentId && !assigneeId) {
      setWorkflowError("Choose a department or assignee.");
      return;
    }
    if (
      ["reassign", "waiting-requester", "waiting-internal", "reopen"].includes(activeAction) &&
      !reason
    ) {
      setWorkflowError("Reason is required.");
      return;
    }
    if (activeAction === "resolve" && !resolutionComment) {
      setWorkflowError("Resolution comment is required.");
      return;
    }

    if (activeAction === "assign") {
      await onWorkflowAction(ticket.id, { type: "assign", payload: { owner_department_id: ownerDepartmentId, assignee_id: assigneeId } });
    } else if (activeAction === "reassign") {
      await onWorkflowAction(ticket.id, {
        type: "reassign",
        payload: { owner_department_id: ownerDepartmentId, assignee_id: assigneeId, reason },
      });
    } else if (activeAction === "waiting-requester") {
      await onWorkflowAction(ticket.id, { type: "waiting-requester", reason });
    } else if (activeAction === "waiting-internal") {
      await onWorkflowAction(ticket.id, { type: "waiting-internal", reason });
    } else if (activeAction === "resolve") {
      await onWorkflowAction(ticket.id, { type: "resolve", resolutionComment });
    } else if (activeAction === "reopen") {
      await onWorkflowAction(ticket.id, { type: "reopen", reason });
    } else if (activeAction === "classify") {
      if (classifySaving) {
        return;
      }
      const classification = workflowForm.classification as CommercialClassification;
      const assessmentNote = workflowForm.assessmentNote.trim();
      if (!classification) {
        setWorkflowError("Classification is required.");
        return;
      }
      if (!assessmentNote) {
        setWorkflowError("Assessment note is required.");
        return;
      }
      const isReclassification = ticket.classification !== null && ticket.classification !== classification;
      if (isReclassification && !reason) {
        setWorkflowError("Reason is required when reclassifying.");
        return;
      }
      const payload: ClassifyTicketPayload = {
        classification: classification,
        assessment_note: assessmentNote,
      };
      if (isReclassification && reason) {
        payload.reason = reason;
      }
      setClassifySaving(true);
      try {
        await onWorkflowAction(ticket.id, {
          type: "classify",
          payload,
        });
        setWorkflowForm((current) => ({ ...current, action: "", reason: "", resolutionComment: "", classification: "", assessmentNote: "" }));
        setWorkflowError("");
      } catch {
        // form values preserved on failure
      } finally {
        setClassifySaving(false);
      }
      return;
    }

    if (activeAction === "chargeable-request") {
      if (chargeableSaving) {
        return;
      }
      const explanation = workflowForm.chargeableExplanation.trim();
      if (!explanation) {
        setWorkflowError("Chargeable explanation is required.");
        return;
      }
      setChargeableSaving(true);
      try {
        await onWorkflowAction(ticket.id, { type: "chargeable-request", explanation });
        setWorkflowForm((current) => ({
          ...current,
          action: "",
          reason: "",
          resolutionComment: "",
          classification: "",
          assessmentNote: "",
          chargeableExplanation: "",
        }));
        setWorkflowError("");
      } catch {
        // form values preserved on failure
      } finally {
        setChargeableSaving(false);
      }
      return;
    }

    setWorkflowForm((current) => ({ ...current, action: "", reason: "", resolutionComment: "", classification: "", assessmentNote: "", chargeableExplanation: "" }));
  }

  if (isRequester) {
    return (
      <div className="space-y-5">
        {createNotice !== null && createNotice.ticketId === ticket.id ? (
          <p
            className={`rounded-md px-4 py-3 text-sm ${createNotice.tone === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}
          >
            {createNotice.message}
          </p>
        ) : null}
        {message ? <p className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</p> : null}
        {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}

        {showChargeableResponseUI ? (
          <section className="rounded-lg border border-amber-200 bg-amber-50 p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-amber-900">Chargeable Confirmation Required</h3>
            <p className="mt-2 text-sm text-amber-800">
              The support team has marked this ticket as chargeable and is awaiting your confirmation.
            </p>
            {ticket.chargeable_explanation ? (
              <div className="mt-3 rounded-md border border-amber-200 bg-white p-3">
                <p className="text-sm font-semibold text-amber-900">Staff Explanation</p>
                <p className="mt-1 text-sm text-amber-800">{ticket.chargeable_explanation}</p>
              </div>
            ) : null}
            {ticket.chargeable_requested_at ? (
              <p className="mt-2 text-xs text-amber-600">
                Requested: {formatDateTime(ticket.chargeable_requested_at)}
              </p>
            ) : null}
            <form className="mt-4 space-y-4" onSubmit={handleSubmitChargeableResponse}>
              {chargeableResponseError ? (
                <p className="rounded-md bg-red-100 px-3 py-2 text-sm text-red-800">
                  {chargeableResponseError}
                </p>
              ) : null}
              <fieldset className="space-y-2" disabled={chargeableResponseSaving}>
                <legend className="text-sm font-semibold text-zinc-800">
                  Please select your response:
                </legend>
                <label className="flex items-center gap-2 text-sm text-zinc-700">
                  <input
                    checked={chargeableResponseOption === "proceed"}
                    className="h-4 w-4 accent-rose-600"
                    name="chargeable-response"
                    onChange={() => setChargeableResponseOption("proceed")}
                    type="radio"
                    value="proceed"
                  />
                  Proceed to Quotation – I would like the team to proceed with quotation/commercial processing.
                </label>
                <label className="flex items-center gap-2 text-sm text-zinc-700">
                  <input
                    checked={chargeableResponseOption === "decline"}
                    className="h-4 w-4 accent-rose-600"
                    name="chargeable-response"
                    onChange={() => setChargeableResponseOption("decline")}
                    type="radio"
                    value="decline"
                  />
                  Decline – I do not agree to the chargeable work.
                </label>
                <label className="flex items-center gap-2 text-sm text-zinc-700">
                  <input
                    checked={chargeableResponseOption === "clarify"}
                    className="h-4 w-4 accent-rose-600"
                    name="chargeable-response"
                    onChange={() => setChargeableResponseOption("clarify")}
                    type="radio"
                    value="clarify"
                  />
                  Clarify – I need more information before deciding.
                </label>
                {chargeableResponseOption === "clarify" ? (
                  <div className="ml-6 mt-2">
                    <label className="block text-sm font-medium text-zinc-700">
                      Clarification question
                      <textarea
                        className="mt-2 min-h-20 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-rose-500"
                        onChange={(event) =>
                          setChargeableClarificationText(event.target.value)
                        }
                        value={chargeableClarificationText}
                      />
                    </label>
                  </div>
                ) : null}
              </fieldset>
              <button
                className="rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={!chargeableResponseOption || chargeableResponseSaving}
                type="submit"
              >
                {chargeableResponseSaving ? "Submitting..." : "Submit Response"}
              </button>
            </form>
          </section>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <button
              className="mb-3 rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-semibold text-zinc-700 shadow-sm hover:bg-zinc-100"
              onClick={onBack}
              type="button"
            >
              Back
            </button>
            <h3 className="text-2xl font-semibold tracking-tight">Ticket: #{ticket.ticket_no}</h3>
            <p className="mt-1 text-sm text-zinc-500">{ticket.title}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className={statusBadgeClass(ticket.status)}>{customerStatusLabel(ticket.status)}</Badge>
            <Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge>
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-5">
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h4 className="text-lg font-semibold">Ticket Details</h4>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <DetailTerm label="Ticket Number" value={ticket.ticket_no} />
                <DetailTerm label="Service Type" value={ticket.request_type_name ?? "-"} />
              </dl>
              <div className="mt-5">
                <h5 className="text-sm font-semibold text-zinc-700">Description</h5>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-zinc-600">{ticket.description}</p>
              </div>
              <TicketAttachmentsSection key={ticket.id} ticket={ticket} user={user} onActivityChanged={onRefreshTimeline} />
            </section>
            {comments.length ? (
              <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold">Comments</h3>
                <div className="mt-4 space-y-3">
                  {comments.map((comment) => (
                    <article className="rounded-md border border-zinc-200 bg-zinc-50/60 p-4" key={comment.id}>
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm font-semibold text-zinc-900">
                          {comment.author_name ?? "Unknown"}{" "}
                          <span className="font-normal text-zinc-500">({customerRoleLabel(comment.author_role)})</span>
                          {comment.is_internal ? (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Internal</span>
                          ) : null}
                        </p>
                        <p className="text-xs text-zinc-500">{formatDateTime(comment.created_at)}</p>
                      </div>
                      <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-zinc-600">{comment.comment_text}</p>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Activity Timeline</h3>
              <div className="mt-5 space-y-4">
                {activity.length ? (
                  activity.map((event) => (
                    <article className="border-l-2 border-rose-200 pl-4" key={event.id}>
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm font-semibold text-zinc-900">{event.event_type}</p>
                        <p className="text-xs text-zinc-500">{formatDateTime(event.created_at)}</p>
                      </div>
                      <p className="mt-1 text-sm text-zinc-600">
                        {event.actor_name ?? "System"} {event.actor_role ? `(${customerRoleLabel(event.actor_role)})` : ""}
                      </p>
                      {formatActivityDetail(event) ? (
                        <p className="mt-2 text-sm leading-6 text-zinc-600">{formatActivityDetail(event)}</p>
                      ) : null}
                    </article>
                  ))
                ) : (
                  <p className="text-sm text-zinc-500">No timeline entries yet.</p>
                )}
              </div>
            </section>
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Add Comment</h3>
              <form className="mt-4" onSubmit={handleSubmit}>
                <label className="block text-sm font-medium text-zinc-700">
                  Comment
                  <textarea
                    className="mt-2 min-h-28 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-rose-500"
                    value={commentText}
                    onChange={(event) => setCommentText(event.target.value)}
                  />
                </label>
                {commentError ? <p className="mt-2 text-sm text-red-700">{commentError}</p> : null}
                <button className="mt-3 rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700">
                  Add Comment
                </button>
              </form>
            </section>
          </div>
          <aside className="space-y-5">
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Ticket Status</h3>
              <dl className="mt-5 space-y-4 text-sm">
                <div>
                  <dt className="font-medium text-zinc-500">Status</dt>
                  <dd className="mt-1"><Badge className={statusBadgeClass(ticket.status)}>{customerStatusLabel(ticket.status)}</Badge></dd>
                </div>
                <div>
                  <dt className="font-medium text-zinc-500">Priority</dt>
                  <dd className="mt-1"><Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge></dd>
                </div>
                <DetailTerm label="Service Type" value={ticket.request_type_name ?? "-"} />
              </dl>
            </section>
            {ownsTicket && ["Resolved", "Closed"].includes(ticket.status) ? (
              <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold">Workflow Actions</h3>
                <div className="mt-4 grid gap-2">
                  {ticket.status === "Resolved" ? (
                    <StaffActionButton onClick={() => onWorkflowAction(ticket.id, { type: "close" })}>Close Ticket</StaffActionButton>
                  ) : null}
                  <StaffActionButton onClick={() => openWorkflowForm("reopen")}>Reopen</StaffActionButton>
                </div>
                {activeAction === "reopen" ? (
                  <form className="mt-5 space-y-4" onSubmit={submitWorkflowForm}>
                    {workflowError ? <p className="text-sm text-red-700">{workflowError}</p> : null}
                    <label className="block text-sm font-medium text-zinc-700">
                      Reason
                      <textarea
                        className="mt-2 min-h-24 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-rose-500"
                        value={workflowForm.reason}
                        onChange={(event) => setWorkflowForm((current) => ({ ...current, reason: event.target.value }))}
                      />
                    </label>
                    <div className="flex justify-end gap-3">
                      <button
                        className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
                        onClick={() => setWorkflowForm((current) => ({ ...current, action: "", reason: "" }))}
                        type="button"
                      >
                        Cancel
                      </button>
                      <button className="rounded-md bg-amber-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-800" type="submit">
                        Reopen Ticket
                      </button>
                    </div>
                  </form>
                ) : null}
              </section>
            ) : null}
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Ticket Information</h3>
              <dl className="mt-5 space-y-4 text-sm">
                <DetailTerm label="Submitted by" value={ticket.requester_name ?? "-"} />
                <DetailTerm label="Department" value={ticket.owner_department_name ?? "-"} />
                <DetailTerm label="Assigned to" value={ticket.assignee_name ?? "Unassigned"} />
                <DetailTerm label="Created" value={formatDateTime(ticket.created_at)} />
                <DetailTerm label="Last Updated" value={formatDateTime(ticket.updated_at)} />
              </dl>
            </section>
          </aside>
        </div>
      </div>
    );
  }

  if (isStaff) {
    return (
      <div className="space-y-5">
        {message ? <p className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</p> : null}
        {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <button
              className="mb-3 rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-semibold text-zinc-700 shadow-sm hover:bg-zinc-100"
              onClick={onBack}
              type="button"
            >
              Back
            </button>
            <h3 className="text-2xl font-semibold tracking-tight">Ticket: #{ticket.ticket_no}</h3>
            <p className="mt-1 text-sm text-zinc-500">{ticket.title}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className={statusBadgeClass(ticket.status)}>{ticket.status}</Badge>
            <Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge>
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-5">
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h4 className="text-lg font-semibold">Ticket Details</h4>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <DetailTerm label="Ticket Number" value={ticket.ticket_no} />
                <DetailTerm label="Service Type" value={ticket.request_type_name ?? "-"} />
                <DetailTerm label="Department / Group" value={ticket.owner_department_name ?? "-"} />
                <DetailTerm label="Requester" value={ticket.requester_name ?? "-"} />
              </dl>
              <div className="mt-5">
                <h5 className="text-sm font-semibold text-zinc-700">Description</h5>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-zinc-600">{ticket.description}</p>
              </div>
              <TicketAttachmentsSection key={ticket.id} ticket={ticket} user={user} onActivityChanged={onRefreshTimeline} />
            </section>
            {comments.length ? (
              <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold">Comments</h3>
                <div className="mt-4 space-y-3">
                  {comments.map((comment) => (
                    <article className="rounded-md border border-zinc-200 bg-zinc-50/60 p-4" key={comment.id}>
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm font-semibold text-zinc-900">
                          {comment.author_name ?? "Unknown"}{" "}
                          <span className="font-normal text-zinc-500">({customerRoleLabel(comment.author_role)})</span>
                          {comment.is_internal ? (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Internal</span>
                          ) : null}
                        </p>
                        <p className="text-xs text-zinc-500">{formatDateTime(comment.created_at)}</p>
                      </div>
                      <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-zinc-600">{comment.comment_text}</p>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Activity Timeline</h3>
              <div className="mt-5 space-y-4">
                {activity.length ? (
                  activity.map((event) => (
                    <article className="border-l-2 border-rose-200 pl-4" key={event.id}>
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm font-semibold text-zinc-900">{event.event_type}</p>
                        <p className="text-xs text-zinc-500">{formatDateTime(event.created_at)}</p>
                      </div>
                      <p className="mt-1 text-sm text-zinc-600">
                        {event.actor_name ?? "System"} {event.actor_role ? `(${customerRoleLabel(event.actor_role)})` : ""}
                      </p>
                      {formatActivityDetail(event) ? (
                        <p className="mt-2 text-sm leading-6 text-zinc-600">{formatActivityDetail(event)}</p>
                      ) : null}
                    </article>
                  ))
                ) : (
                  <p className="text-sm text-zinc-500">No timeline entries yet.</p>
                )}
              </div>
            </section>
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Add Comment</h3>
              <form className="mt-4" onSubmit={handleSubmit}>
                <label className="block text-sm font-medium text-zinc-700">
                  Comment
                  <textarea
                    className="mt-2 min-h-28 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-rose-500"
                    value={commentText}
                    onChange={(event) => setCommentText(event.target.value)}
                  />
                </label>
                <label className="mt-3 block max-w-xs text-sm font-medium text-zinc-700">
                  Comment visibility
                  <select
                    aria-label="Comment visibility"
                    className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                    onChange={(event) => setCommentIsInternal(event.target.value === "internal")}
                    value={commentIsInternal ? "internal" : "customer"}
                  >
                    <option value="customer">Customer-visible</option>
                    <option value="internal">Internal</option>
                  </select>
                </label>
                {commentError ? <p className="mt-2 text-sm text-red-700">{commentError}</p> : null}
                <button className="mt-3 rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700">
                  Add Comment
                </button>
              </form>
            </section>
          </div>
          <aside className="space-y-5">
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Ticket Status</h3>
              <dl className="mt-5 space-y-4 text-sm">
                <div>
                  <dt className="font-medium text-zinc-500">Status</dt>
                  <dd className="mt-1"><Badge className={statusBadgeClass(ticket.status)}>{ticket.status}</Badge></dd>
                </div>
                <div>
                  <dt className="font-medium text-zinc-500">Priority</dt>
                  <dd className="mt-1"><Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge></dd>
                </div>
                <div>
                  <dt className="font-medium text-zinc-500">Classification</dt>
                  <dd className="mt-1">
                    {ticket.classification ? (
                      <Badge className={classificationBadgeClass(ticket.classification)}>
                        {classificationLabel(ticket.classification)}
                      </Badge>
                    ) : (
                      <Badge className={classificationBadgeClass(null)}>Unclassified</Badge>
                    )}
                  </dd>
                </div>
                <DetailTerm label="Service Type" value={ticket.request_type_name ?? "-"} />
              </dl>
            </section>
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Classification</h3>
              <p className="mt-2 text-sm text-zinc-500">
                {ticket.classification
                  ? `This ticket is classified as ${classificationLabel(ticket.classification)}.`
                  : "This ticket is unclassified. Select a classification."}
              </p>
              {ticket.assessment_note ? (
                <p className="mt-2 text-xs text-zinc-500">
                  Assessment: {ticket.assessment_note}
                </p>
              ) : null}
              {canClassify ? (
                <div className="mt-3">
                  <StaffActionButton onClick={() => openWorkflowForm("classify")}>
                    {ticket.classification ? "Update Classification" : "Classify Ticket"}
                  </StaffActionButton>
                </div>
              ) : null}
            </section>
            {canIssueChargeableRequest ? (
              <div className="mt-3">
                <StaffActionButton onClick={() => openWorkflowForm("chargeable-request")}>
                  {ticket.chargeable_request_state === "pending"
                    ? "Resend Chargeable Request"
                    : "Request Chargeable Confirmation"}
                </StaffActionButton>
              </div>
            ) : null}
            {ticket.chargeable_request_state === "pending" && canIssueChargeableRequest ? (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
                <p className="font-semibold text-amber-800">
                  Chargeable confirmation is awaiting the requester.
                </p>
                {ticket.chargeable_explanation ? (
                  <p className="mt-1 text-amber-700">
                    Explanation: {ticket.chargeable_explanation}
                  </p>
                ) : null}
                {ticket.chargeable_requested_at ? (
                  <p className="mt-1 text-xs text-amber-600">
                    Requested: {formatDateTime(ticket.chargeable_requested_at)}
                  </p>
                ) : null}
              </div>
            ) : null}
            {activeAction === "classify" ? (
              <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold">
                  {ticket.classification ? "Update Classification" : "Classify Ticket"}
                </h3>
                <form className="mt-4 space-y-4" onSubmit={submitWorkflowForm}>
                  {workflowError ? (
                    <p className="text-sm text-red-700">{workflowError}</p>
                  ) : null}
                  <label className="block text-sm font-medium text-zinc-700" htmlFor="classification-staff">
                    Classification
                    <select
                      id="classification-staff"
                      className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                      value={workflowForm.classification}
                      onChange={(event) =>
                        setWorkflowForm((current) => ({ ...current, classification: event.target.value }))
                      }
                      aria-label="Classification"
                      role="combobox"
                    >
                      <option value="" disabled>
                        Select classification
                      </option>
                      <option value="FOC">Free of Charge</option>
                      <option value="WARRANTY">Warranty</option>
                      <option value="CHARGEABLE">Chargeable</option>
                      {ticket.classification ? (
                        <option value="UNCLASSIFIED">Unclassified (revert)</option>
                      ) : null}
                    </select>
                  </label>
                  <label className="block text-sm font-medium text-zinc-700" htmlFor="assessment-staff">
                    Assessment note
                    <textarea
                      className="mt-2 min-h-24 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                      placeholder="Enter assessment note"
                      value={workflowForm.assessmentNote}
                      onChange={(event) =>
                        setWorkflowForm((current) => ({ ...current, assessmentNote: event.target.value }))
                      }
                    />
                  </label>
                  {ticket.classification ? (
                    <label className="block text-sm font-medium text-zinc-700">
                      Reason (required when changing or reverting classification)
                      <textarea
                        className="mt-2 min-h-24 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                        placeholder="Reason for changing classification"
                        value={workflowForm.reason}
                        onChange={(event) =>
                          setWorkflowForm((current) => ({ ...current, reason: event.target.value }))
                        }
                      />
                    </label>
                  ) : null}
                  <div className="flex justify-end gap-3">
                    <button
                      className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
                      onClick={() =>
                        setWorkflowForm((current) => ({ ...current, action: "" }))
                      }
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      className="rounded-md bg-sky-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-sky-700"
                      type="submit"
                      disabled={classifySaving && activeAction === "classify"}
                    >
                      {classifySaving && activeAction === "classify" ? "Saving..." : "Save"}
                    </button>
                  </div>
                </form>
              </section>
            ) : null}
            {activeAction === "chargeable-request" ? (
              <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold">
                  {ticket.chargeable_request_state === "pending"
                    ? "Resend Chargeable Request"
                    : "Request Chargeable Confirmation"}
                </h3>
                <form className="mt-4 space-y-4" onSubmit={submitWorkflowForm}>
                  {workflowError ? (
                    <p className="text-sm text-red-700">{workflowError}</p>
                  ) : null}
                  <label className="block text-sm font-medium text-zinc-700">
                    Explanation
                    <textarea
                      className="mt-2 min-h-24 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                      placeholder="Explanation: why this work is chargeable..."
                      value={workflowForm.chargeableExplanation}
                      onChange={(event) =>
                        setWorkflowForm((current) => ({ ...current, chargeableExplanation: event.target.value }))
                      }
                    />
                  </label>
                  <div className="flex justify-end gap-3">
                    <button
                      className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
                      onClick={() =>
                        setWorkflowForm((current) => ({ ...current, action: "" }))
                      }
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      className="rounded-md bg-sky-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-sky-700"
                      data-testid="chargeable-request-submit"
                      type="submit"
                      disabled={chargeableSaving}
                    >
                      {chargeableSaving ? "Sending..." : "Submit"}
                    </button>
                  </div>
                </form>
              </section>
            ) : null}
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Workflow Actions</h3>
              <div className="mt-4 grid gap-2">
                {canTake ? <StaffActionButton onClick={() => onWorkflowAction(ticket.id, { type: "take" })}>Take Ticket</StaffActionButton> : null}
                {canStartProgress ? (
                  <StaffActionButton onClick={() => onWorkflowAction(ticket.id, { type: "start" })}>Start Progress</StaffActionButton>
                ) : null}
                {staffCanAct ? <StaffActionButton onClick={() => setStaffReassignTicket(ticket)}>Reassign</StaffActionButton> : null}
                {staffCanAct && ticket.status === "In Progress" && !isTerminalTicketStatus(ticket.status) ? (
                  <StaffActionButton
                    onClick={() =>
                      onWorkflowAction(ticket.id, {
                        type: "resolve",
                        resolutionComment: "Marked resolved from the Staff ticket detail.",
                      })
                    }
                  >
                    Mark Resolved
                  </StaffActionButton>
                ) : null}
                {staffCanAct && ticket.status === "Resolved" ? (
                  <StaffActionButton onClick={() => onWorkflowAction(ticket.id, { type: "close" })}>Close Ticket</StaffActionButton>
                ) : null}
              </div>
            </section>
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Ticket Information</h3>
              <dl className="mt-5 space-y-4 text-sm">
                <DetailTerm label="Submitted by" value={ticket.requester_name ?? "-"} />
                <DetailTerm label="Department" value={ticket.owner_department_name ?? "-"} />
                <DetailTerm label="Assigned to" value={ticket.assignee_name ?? "Unassigned"} />
                <DetailTerm label="Created" value={formatDateTime(ticket.created_at)} />
                <DetailTerm label="Last Updated" value={formatDateTime(ticket.updated_at)} />
              </dl>
            </section>
          </aside>
        </div>
        {staffReassignTicket ? (
          <ReassignTicketModal
            departmentUsers={users}
            departments={departments}
            onClose={() => setStaffReassignTicket(null)}
            onSubmit={async (payload) => {
              await onWorkflowAction(staffReassignTicket.id, { type: "reassign", payload });
              setStaffReassignTicket(null);
            }}
            ticket={staffReassignTicket}
            user={user}
          />
        ) : null}
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div className="space-y-5">
        {message ? <p className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</p> : null}
        {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <button
              className="mb-3 rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-semibold text-zinc-700 shadow-sm hover:bg-zinc-100"
              onClick={onBack}
              type="button"
            >
              Back
            </button>
            <h3 className="text-2xl font-semibold tracking-tight">Ticket: #{ticket.ticket_no}</h3>
            <p className="mt-1 text-sm text-zinc-500">{ticket.title}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className={statusBadgeClass(ticket.status)}>{ticket.status}</Badge>
            <Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge>
            {ticket.assignee_id === null ? <Badge className="bg-sky-50 text-sky-700">Unassigned</Badge> : null}
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-5">
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h4 className="text-lg font-semibold">Ticket Details</h4>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <DetailTerm label="Ticket Number" value={ticket.ticket_no} />
                <DetailTerm label="Service Type" value={ticket.request_type_name ?? "-"} />
                <DetailTerm label="Department / Group" value={ticket.owner_department_name ?? "-"} />
                <DetailTerm label="Requester" value={ticket.requester_name ?? "-"} />
                <DetailTerm label="Target Date" value={formatDate(ticket.target_date)} />
                <DetailTerm label="Aging" value={`${ticket.aging_days} days`} />
              </dl>
              <div className="mt-5">
                <h5 className="text-sm font-semibold text-zinc-700">Description</h5>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-zinc-600">{ticket.description}</p>
              </div>
              <TicketAttachmentsSection key={ticket.id} ticket={ticket} user={user} onActivityChanged={onRefreshTimeline} />
            </section>
            {comments.length ? (
              <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold">Comments</h3>
                <div className="mt-4 space-y-3">
                  {comments.map((comment) => (
                    <article className="rounded-md border border-zinc-200 bg-zinc-50/60 p-4" key={comment.id}>
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm font-semibold text-zinc-900">
                          {comment.author_name ?? "Unknown"}{" "}
                          <span className="font-normal text-zinc-500">({comment.author_role ?? "User"})</span>
                          {comment.is_internal ? (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Internal</span>
                          ) : null}
                        </p>
                        <p className="text-xs text-zinc-500">{formatDateTime(comment.created_at)}</p>
                      </div>
                      <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-zinc-600">{comment.comment_text}</p>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Activity Timeline</h3>
              <div className="mt-5 space-y-4">
                {activity.length ? (
                  activity.map((event) => (
                    <article className="border-l-2 border-rose-200 pl-4" key={event.id}>
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm font-semibold text-zinc-900">{event.event_type}</p>
                        <p className="text-xs text-zinc-500">{formatDateTime(event.created_at)}</p>
                      </div>
                      <p className="mt-1 text-sm text-zinc-600">
                        {event.actor_name ?? "System"} {event.actor_role ? `(${event.actor_role})` : ""}
                      </p>
                      {formatActivityDetail(event) ? (
                        <p className="mt-2 text-sm leading-6 text-zinc-600">{formatActivityDetail(event)}</p>
                      ) : null}
                    </article>
                  ))
                ) : (
                  <p className="text-sm text-zinc-500">No timeline entries yet.</p>
                )}
              </div>
            </section>
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Add Comment</h3>
              <form className="mt-4" onSubmit={handleSubmit}>
                <label className="block text-sm font-medium text-zinc-700">
                  Comment
                  <textarea
                    className="mt-2 min-h-28 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-rose-500"
                    value={commentText}
                    onChange={(event) => setCommentText(event.target.value)}
                  />
                </label>
                <label className="mt-3 block max-w-xs text-sm font-medium text-zinc-700">
                  Comment visibility
                  <select
                    aria-label="Comment visibility"
                    className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                    onChange={(event) => setCommentIsInternal(event.target.value === "internal")}
                    value={commentIsInternal ? "internal" : "customer"}
                  >
                    <option value="customer">Customer-visible</option>
                    <option value="internal">Internal</option>
                  </select>
                </label>
                {commentError ? <p className="mt-2 text-sm text-red-700">{commentError}</p> : null}
                <button className="mt-3 rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700">
                  Add Comment
                </button>
              </form>
            </section>
          </div>
          <aside className="space-y-5">
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Ticket Status</h3>
              <dl className="mt-5 space-y-4 text-sm">
                <div>
                  <dt className="font-medium text-zinc-500">Status</dt>
                  <dd className="mt-1"><Badge className={statusBadgeClass(ticket.status)}>{ticket.status}</Badge></dd>
                </div>
                <div>
                  <dt className="font-medium text-zinc-500">Priority</dt>
                  <dd className="mt-1"><Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge></dd>
                </div>
                <div>
                  <dt className="font-medium text-zinc-500">Classification</dt>
                  <dd className="mt-1">
                    {ticket.classification ? (
                      <Badge className={classificationBadgeClass(ticket.classification)}>
                        {classificationLabel(ticket.classification)}
                      </Badge>
                    ) : (
                      <Badge className={classificationBadgeClass(null)}>Unclassified</Badge>
                    )}
                  </dd>
                </div>
                <DetailTerm label="Service Type" value={ticket.request_type_name ?? "-"} />
              </dl>
            </section>
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Classification</h3>
              <p className="mt-2 text-sm text-zinc-500">
                {ticket.classification
                  ? `This ticket is classified as ${classificationLabel(ticket.classification)}.`
                  : "This ticket is unclassified. Select a classification."}
              </p>
              {ticket.assessment_note ? (
                <p className="mt-2 text-xs text-zinc-500">
                  Assessment: {ticket.assessment_note}
                </p>
              ) : null}
              <div className="mt-3">
                <StaffActionButton onClick={() => openWorkflowForm("classify")}>
                  {ticket.classification ? "Update Classification" : "Classify Ticket"}
                </StaffActionButton>
              </div>
            </section>
            {activeAction === "classify" ? (
              <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold">
                  {ticket.classification ? "Update Classification" : "Classify Ticket"}
                </h3>
                <form className="mt-4 space-y-4" onSubmit={submitWorkflowForm}>
                  {workflowError ? (
                    <p className="text-sm text-red-700">{workflowError}</p>
                  ) : null}
                  <label className="block text-sm font-medium text-zinc-700" htmlFor="classification-admin">
                    Classification
                    <select
                      id="classification-admin"
                      className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                      value={workflowForm.classification}
                      onChange={(event) =>
                        setWorkflowForm((current) => ({ ...current, classification: event.target.value }))
                      }
                      aria-label="Classification"
                      role="combobox"
                    >
                      <option value="" disabled>
                        Select classification
                      </option>
                      <option value="FOC">Free of Charge</option>
                      <option value="WARRANTY">Warranty</option>
                      <option value="CHARGEABLE">Chargeable</option>
                      {ticket.classification ? (
                        <option value="UNCLASSIFIED">Unclassified (revert)</option>
                      ) : null}
                    </select>
                  </label>
                  <label className="block text-sm font-medium text-zinc-700">
                    Assessment Note
                    <textarea
                      className="mt-2 min-h-[80px] w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                      placeholder="Enter assessment note"
                      value={workflowForm.assessmentNote}
                      onChange={(event) =>
                        setWorkflowForm((current) => ({ ...current, assessmentNote: event.target.value }))
                      }
                    />
                  </label>
                  {ticket.classification ? (
                    <label className="block text-sm font-medium text-zinc-700">
                      Reason
                      <input
                        className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                        placeholder="Reason for changing classification"
                        value={workflowForm.reason}
                        onChange={(event) =>
                          setWorkflowForm((current) => ({ ...current, reason: event.target.value }))
                        }
                      />
                    </label>
                  ) : null}
                  <div className="flex justify-end gap-3">
                    <button
                      className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
                      onClick={() =>
                        setWorkflowForm((current) => ({ ...current, action: "" }))
                      }
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      className="rounded-md bg-sky-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-sky-700"
                      type="submit"
                      disabled={classifySaving && activeAction === "classify"}
                    >
                      {classifySaving && activeAction === "classify" ? "Saving..." : "Save"}
                    </button>
                  </div>
                </form>
              </section>
            ) : null}
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Workflow Actions</h3>
              <div className="mt-4 grid gap-2">
                <StaffActionButton onClick={() => setAdminAssignmentTicket(ticket)}>
                  {ticket.assignee_id ? "Reassign Ticket" : "Assign Ticket"}
                </StaffActionButton>
                {ticket.assignee_id ? (
                  <StaffActionButton onClick={() => onWorkflowAction(ticket.id, { type: "unassign" })}>Unassign Ticket</StaffActionButton>
                ) : null}
                {ticket.status === "Resolved" ? (
                  <StaffActionButton onClick={() => onWorkflowAction(ticket.id, { type: "close" })}>Close Ticket</StaffActionButton>
                ) : null}
                {["Resolved", "Closed"].includes(ticket.status) ? (
                  <StaffActionButton onClick={() => openWorkflowForm("reopen")}>Reopen</StaffActionButton>
                ) : null}
              </div>
            </section>
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold">Ticket Information</h3>
              <dl className="mt-5 space-y-4 text-sm">
                <DetailTerm label="Submitted by" value={ticket.requester_name ?? "-"} />
                <DetailTerm label="Department" value={ticket.owner_department_name ?? "-"} />
                <DetailTerm label="Assigned to" value={ticket.assignee_name ?? "Unassigned"} />
                <DetailTerm label="Created" value={formatDateTime(ticket.created_at)} />
                <DetailTerm label="Last Updated" value={formatDateTime(ticket.updated_at)} />
                <DetailTerm label="Reopen Count" value={String(ticket.reopen_count)} />
              </dl>
            </section>
          </aside>
        </div>
        {adminAssignmentTicket ? (
          <AdminAssignTicketModal
            departments={departments}
            onClose={() => setAdminAssignmentTicket(null)}
            onSubmit={async (action) => {
              await onWorkflowAction(adminAssignmentTicket.id, action);
              setAdminAssignmentTicket(null);
            }}
            ticket={adminAssignmentTicket}
            user={user}
            users={users}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-6">
      {message ? <p className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</p> : null}
      {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
      <section className="rounded-lg border border-zinc-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-sky-700">{ticket.ticket_no}</p>
            <h3 className="mt-1 text-2xl font-semibold">{ticket.title}</h3>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge className={statusBadgeClass(ticket.status)}>{ticket.status}</Badge>
            <Badge className={priorityBadgeClass(ticket.priority)}>{ticket.priority}</Badge>
            {ticket.overdue ? <Badge className="bg-red-50 text-red-700">Overdue</Badge> : null}
          </div>
        </div>
        <dl className="mt-6 grid gap-4 text-sm md:grid-cols-3">
          <DetailTerm label="Request Type" value={ticket.request_type_name ?? "-"} />
          <DetailTerm label="Department / Group" value={ticket.owner_department_name ?? "-"} />
          <DetailTerm label="Requester" value={ticket.requester_name ?? "-"} />
          <DetailTerm label="Assignee" value={ticket.assignee_name ?? "-"} />
          <DetailTerm label="Target Date" value={formatDate(ticket.target_date)} />
          <DetailTerm label="Aging" value={`${ticket.aging_days} days`} />
          <DetailTerm label="Created" value={formatDateTime(ticket.created_at)} />
          <DetailTerm label="Last Activity" value={formatDateTime(ticket.last_activity_at)} />
          <DetailTerm label="Reopen Count" value={String(ticket.reopen_count)} />
        </dl>
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-zinc-700">Description</h4>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-zinc-600">{ticket.description}</p>
        </div>
      </section>
      <section className="rounded-lg border border-zinc-200 bg-white p-6">
        <h3 className="text-lg font-semibold">Workflow Actions</h3>
        <div className="mt-4 flex flex-wrap gap-3">
          {isAdmin ? (
            <>
              <button className="rounded-md bg-zinc-900 px-4 py-2 text-sm font-semibold text-white" onClick={() => openWorkflowForm("assign")}>
                Assign
              </button>
              <button className="rounded-md bg-zinc-900 px-4 py-2 text-sm font-semibold text-white" onClick={() => openWorkflowForm("reassign")}>
                Reassign
              </button>
            </>
          ) : null}
          {canTake ? (
            <button className="rounded-md bg-sky-700 px-4 py-2 text-sm font-semibold text-white" onClick={() => onWorkflowAction(ticket.id, { type: "take" })}>
              Take Ticket
            </button>
          ) : null}
          {canStartProgress ? (
            <button className="rounded-md bg-sky-700 px-4 py-2 text-sm font-semibold text-white" onClick={() => onWorkflowAction(ticket.id, { type: "start" })}>
              Start Progress
            </button>
          ) : null}
          {staffCanAct && ticket.status === "In Progress" ? (
            <>
              <button className="rounded-md bg-zinc-700 px-4 py-2 text-sm font-semibold text-white" onClick={() => openWorkflowForm("waiting-requester")}>
                Waiting for Requester
              </button>
              <button className="rounded-md bg-zinc-700 px-4 py-2 text-sm font-semibold text-white" onClick={() => openWorkflowForm("waiting-internal")}>
                Waiting Internal
              </button>
              <button className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white" onClick={() => openWorkflowForm("resolve")}>
                Resolve
              </button>
            </>
          ) : null}
          {staffCanAct && ["Waiting for Requester", "Waiting Internal"].includes(ticket.status) ? (
            <button className="rounded-md bg-sky-700 px-4 py-2 text-sm font-semibold text-white" onClick={() => onWorkflowAction(ticket.id, { type: "return-in-progress" })}>
              Return to In Progress
            </button>
          ) : null}
          {((isRequester && ownsTicket) || isAdmin) && ["Resolved", "Closed"].includes(ticket.status) ? (
            <button className="rounded-md bg-amber-700 px-4 py-2 text-sm font-semibold text-white" onClick={() => openWorkflowForm("reopen")}>
              Reopen
            </button>
          ) : null}
        </div>
        {activeAction ? (
          <form className="mt-5 space-y-4 rounded-md bg-zinc-50 p-4" onSubmit={submitWorkflowForm}>
            {["assign", "reassign"].includes(activeAction) ? (
              <div className="grid gap-4 md:grid-cols-2">
                <label className="block text-sm font-medium text-zinc-700">
                  Department
                  <select
                    className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                    value={workflowForm.ownerDepartmentId}
                    onChange={(event) => setWorkflowForm((current) => ({ ...current, ownerDepartmentId: event.target.value }))}
                  >
                    <option value="">No change</option>
                    {departments.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm font-medium text-zinc-700">
                  Assignee
                  <select
                    className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                    value={workflowForm.assigneeId}
                    onChange={(event) => setWorkflowForm((current) => ({ ...current, assigneeId: event.target.value }))}
                  >
                    <option value="">Unassigned / no change</option>
                    {users
                      .filter((managedUser) => managedUser.is_active && ["Staff", "Admin"].includes(managedUser.role))
                      .map((managedUser) => (
                        <option key={managedUser.id} value={managedUser.id}>
                          {managedUser.full_name} ({managedUser.role})
                        </option>
                      ))}
                  </select>
                </label>
              </div>
            ) : null}
            {["reassign", "waiting-requester", "waiting-internal", "reopen"].includes(activeAction) ? (
              <label className="block text-sm font-medium text-zinc-700">
                Reason
                <textarea
                  className="mt-2 min-h-24 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                  value={workflowForm.reason}
                  onChange={(event) => setWorkflowForm((current) => ({ ...current, reason: event.target.value }))}
                />
              </label>
            ) : null}
            {activeAction === "resolve" ? (
              <label className="block text-sm font-medium text-zinc-700">
                Resolution Comment
                <textarea
                  className="mt-2 min-h-24 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                  value={workflowForm.resolutionComment}
                  onChange={(event) =>
                    setWorkflowForm((current) => ({ ...current, resolutionComment: event.target.value }))
                  }
                />
              </label>
            ) : null}
            {activeAction === "classify" ? (
              <>
                <label className="block text-sm font-medium text-zinc-700" htmlFor="classification-fallback">
                  Classification
                    <select
                    id="classification-fallback"
                    className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                    value={workflowForm.classification}
                    onChange={(event) =>
                      setWorkflowForm((current) => ({ ...current, classification: event.target.value }))
                    }
                    aria-label="Classification"
                    role="combobox"
                  >
                    <option value="" disabled>
                      Select classification
                    </option>
                    <option value="FOC">Free of Charge</option>
                    <option value="WARRANTY">Warranty</option>
                    <option value="CHARGEABLE">Chargeable</option>
                    {ticket.classification ? (
                      <option value="UNCLASSIFIED">Unclassified (revert)</option>
                    ) : null}
                  </select>
                </label>
                <label className="block text-sm font-medium text-zinc-700">
                  Assessment Note
                  <textarea
                    className="mt-2 min-h-24 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                    value={workflowForm.assessmentNote}
                    onChange={(event) =>
                      setWorkflowForm((current) => ({ ...current, assessmentNote: event.target.value }))
                    }
                  />
                </label>
                {ticket.classification !== null ? (
                  <label className="block text-sm font-medium text-zinc-700">
                    Reason
                    <textarea
                      className="mt-2 min-h-24 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
                      value={workflowForm.reason}
                      onChange={(event) =>
                        setWorkflowForm((current) => ({ ...current, reason: event.target.value }))
                      }
                    />
                  </label>
                ) : null}
              </>
            ) : null}
            {workflowError ? <p className="text-sm text-red-700">{workflowError}</p> : null}
            <div className="flex gap-3">
              <button
                className="rounded-md bg-sky-700 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                type="submit"
                disabled={classifySaving && activeAction === "classify"}
              >
                {classifySaving && activeAction === "classify" ? "Saving..." : "Submit"}
              </button>
              <button
                className="rounded-md border border-zinc-300 px-4 py-2 text-sm font-semibold text-zinc-700"
                onClick={() => setWorkflowForm((current) => ({ ...current, action: "" }))}
                type="button"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}
      </section>
      <section className="rounded-lg border border-zinc-200 bg-white p-6">
        <h3 className="text-lg font-semibold">Comments</h3>
        <div className="mt-5 space-y-4">
          {comments.length ? (
            comments.map((comment) => (
              <article className="rounded-md border border-zinc-200 p-4" key={comment.id}>
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm font-semibold text-zinc-900">
                    {comment.author_name ?? "Unknown"}{" "}
                    <span className="font-normal text-zinc-500">({comment.author_role ?? "User"})</span>
                  </p>
                  <p className="text-xs text-zinc-500">{formatDateTime(comment.created_at)}</p>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-zinc-600">{comment.comment_text}</p>
              </article>
            ))
          ) : (
            <p className="text-sm text-zinc-500">No comments yet.</p>
          )}
        </div>
        <form className="mt-5" onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-zinc-700">
            Add Comment
            <textarea
              className="mt-2 min-h-28 w-full rounded-md border border-zinc-300 px-3 py-2 text-zinc-950 outline-none focus:border-sky-600"
              value={commentText}
              onChange={(event) => setCommentText(event.target.value)}
            />
          </label>
          {commentError ? <p className="mt-2 text-sm text-red-700">{commentError}</p> : null}
          <button className="mt-3 rounded-md bg-sky-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-800">
            Add Comment
          </button>
        </form>
      </section>
      <section className="rounded-lg border border-zinc-200 bg-white p-6">
        <h3 className="text-lg font-semibold">Activity Timeline</h3>
        <div className="mt-5 space-y-4">
          {activity.map((event) => (
            <article className="border-l-2 border-sky-200 pl-4" key={event.id}>
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm font-semibold text-zinc-900">{event.event_type}</p>
                <p className="text-xs text-zinc-500">{formatDateTime(event.created_at)}</p>
              </div>
              <p className="mt-1 text-sm text-zinc-600">
                {event.actor_name ?? "System"} {event.actor_role ? `(${event.actor_role})` : ""}
              </p>
              {formatActivityDetail(event) ? (
                <p className="mt-2 text-sm leading-6 text-zinc-600">{formatActivityDetail(event)}</p>
              ) : null}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function PasswordChangeSection({
  onChangeSuccess,
  submitLabel = "Change Password",
}: {
  onChangeSuccess?: () => void;
  submitLabel?: string;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  async function handlePasswordChange(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation must match.");
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordSuccess("Password changed successfully.");
      if (onChangeSuccess) {
        onChangeSuccess();
      }
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : "Unable to change password. Please try again.");
    } finally {
      setIsChangingPassword(false);
    }
  }

  return (
    <form onSubmit={handlePasswordChange}>
      <div className="grid gap-4 md:grid-cols-3">
        <PasswordField label="Current Password" value={currentPassword} onChange={setCurrentPassword} />
        <PasswordField label="New Password" value={newPassword} onChange={setNewPassword} />
        <PasswordField label="Confirm Password" value={confirmPassword} onChange={setConfirmPassword} />
      </div>
      {passwordError ? <p className="mt-3 text-sm text-red-700" role="alert">{passwordError}</p> : null}
      {passwordSuccess ? <p className="mt-3 text-sm text-emerald-700" aria-live="polite">{passwordSuccess}</p> : null}
      <button
        className="mt-5 rounded-md bg-sky-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-800 disabled:bg-zinc-300"
        disabled={isChangingPassword}
        type="submit"
      >
        {isChangingPassword ? "Changing password..." : submitLabel}
      </button>
    </form>
  );
}

function ProfilePage({
  allowPasswordChange,
  companyName,
  departmentName,
  isCustomer,
  user,
}: {
  allowPasswordChange: boolean;
  companyName: string;
  departmentName: string;
  isCustomer: boolean;
  user: CurrentUser;
}) {
  return (
    <section className="space-y-5">
      <div>
        <h3 className="text-2xl font-semibold tracking-tight">Profile</h3>
        <p className="mt-1 text-sm text-zinc-500">Manage your account information and authentication settings.</p>
      </div>
      <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <AvatarInitials name={user.full_name} size="xl" />
            <div>
              <h4 className="text-lg font-semibold text-zinc-950">{user.full_name}</h4>
              <p className="text-sm text-zinc-500">{user.email}</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-wide text-emerald-700">
                {isCustomer ? "Customer" : user.role}
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
        <h4 className="text-lg font-semibold">Profile Information</h4>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <ReadOnlyField label="Full Name" value={user.full_name} />
          <ReadOnlyField label="Email" value={user.email} />
          <ReadOnlyField label="Department" value={departmentName} />
          <ReadOnlyField label="Company" value={companyName} />
        </div>
      </section>
      {allowPasswordChange ? (
        <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
          <h4 className="text-lg font-semibold">Password & Authentication</h4>
          <PasswordChangeSection />
        </section>
      ) : null}
    </section>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <label className="block text-sm font-medium text-zinc-700">
      {label}
      <input
        className="mt-2 w-full rounded-md border border-zinc-300 bg-zinc-50 px-3 py-2.5 text-zinc-700"
        readOnly
        value={value}
      />
    </label>
  );
}

function PasswordField({
  label,
  onChange,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className="block text-sm font-medium text-zinc-700">
      {label}
      <input
        className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-sky-600"
        onChange={(event) => onChange(event.target.value)}
        required
        type="password"
        value={value}
      />
    </label>
  );
}

function AvatarInitials({ name, size = "md" }: { name: string; size?: "md" | "lg" | "xl" }) {
  const sizeClass = size === "xl" ? "h-20 w-20 text-xl" : size === "lg" ? "h-12 w-12 text-base" : "h-10 w-10 text-sm";

  return (
    <div className={`flex shrink-0 items-center justify-center rounded-full bg-emerald-100 font-bold text-emerald-900 ring-1 ring-emerald-200 ${sizeClass}`}>
      {initialsForName(name)}
    </div>
  );
}

function initialsForName(name: string) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return initials || "U";
}

function defaultTargetDate() {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().slice(0, 10);
}

function customerStatusLabel(status: string) {
  if (["New", "Assigned", "Reopened"].includes(status)) {
    return "Open";
  }
  if (["In Progress", "Waiting Internal", "Waiting for Requester"].includes(status)) {
    return "In Progress";
  }
  return status;
}

function customerTicketMatchesFilter(ticket: TicketListItem, filter: CustomerTicketFilter) {
  if (filter === "total") {
    return true;
  }
  if (filter === "open") {
    return ["New", "Assigned", "Reopened"].includes(ticket.status);
  }
  if (filter === "in_progress") {
    return ["In Progress", "Waiting Internal", "Waiting for Requester"].includes(ticket.status);
  }
  return ticket.status === "Resolved";
}

function staffTabForPage(page: PageKey): StaffTicketTab {
  if (page === "available-tickets") {
    return "available";
  }
  if (page === "my-tickets") {
    return "submitted";
  }
  return "assigned";
}

function pageForStaffTab(tab: StaffTicketTab): PageKey {
  if (tab === "available") {
    return "available-tickets";
  }
  if (tab === "submitted") {
    return "my-tickets";
  }
  return "my-assigned-tickets";
}

function staffEmptyText(tab: StaffTicketTab) {
  if (tab === "available") {
    return "No available tickets match these filters.";
  }
  if (tab === "submitted") {
    return "No submitted tickets match these filters.";
  }
  return "No assigned tickets match these filters.";
}

function isTerminalTicketStatus(status: string) {
  return ["Resolved", "Closed"].includes(status);
}

function filterTicketsForStaff(tickets: TicketListItem[], filters: TicketFilters) {
  const search = filters.search?.trim().toLocaleLowerCase();

  return tickets.filter((ticket) => {
    const matchesSearch =
      !search ||
      ticket.ticket_no.toLocaleLowerCase().includes(search) ||
      ticket.title.toLocaleLowerCase().includes(search) ||
      (ticket.requester_name ?? "").toLocaleLowerCase().includes(search);
    const matchesStatus = !filters.status || ticket.status === filters.status;
    const matchesPriority = !filters.priority || ticket.priority === filters.priority;
    const matchesClassification = !filters.classification || ticket.classification === filters.classification;

    return matchesSearch && matchesStatus && matchesPriority && matchesClassification;
  });
}

function customerRoleLabel(role: string | null) {
  if (role === "Requester") {
    return "Customer";
  }

  return role ?? "User";
}

function formatDate(value: string | null) {
  if (!value) {
    return "-";
  }

  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return [
    String(date.getDate()).padStart(2, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    date.getFullYear(),
  ].join("/");
}

function formatDateTime(value: string | null) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  const hours = date.getHours();
  const displayHours = hours % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const period = hours >= 12 ? "PM" : "AM";

  return `${formatDate(value)}, ${displayHours}:${minutes} ${period}`;
}

function formatActivityDetail(event: TicketActivity) {
  if (event.old_value && event.new_value) {
    const transition = `${event.old_value} -> ${event.new_value}`;
    return event.remarks ? `${transition}. ${event.remarks}` : transition;
  }

  return event.remarks;
}

function Badge({ children, className }: { children: string; className: string }) {
  return (
    <span className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${className}`}>
      {children}
    </span>
  );
}

function InlineMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <dt className="font-semibold text-zinc-400">{label}</dt>
      <dd className="font-medium text-zinc-700">{value}</dd>
    </div>
  );
}

function statusBadgeClass(status: string) {
  if (status === "Closed") {
    return "bg-zinc-100 text-zinc-700";
  }
  if (status === "Resolved") {
    return "bg-emerald-50 text-emerald-700";
  }
  if (status === "Waiting for Requester" || status === "Waiting Internal") {
    return "bg-amber-50 text-amber-800";
  }
  if (status === "Reopened") {
    return "bg-violet-50 text-violet-700";
  }
  return "bg-sky-50 text-sky-700";
}

function priorityBadgeClass(priority: string) {
  if (priority === "Critical") {
    return "bg-red-50 text-red-700";
  }
  if (priority === "High") {
    return "bg-orange-50 text-orange-700";
  }
  if (priority === "Low") {
    return "bg-zinc-100 text-zinc-700";
  }
  return "bg-blue-50 text-blue-700";
}

function classificationLabel(value: string, fallback: string = "Unclassified"): string {
  if (value === "FOC") {
    return "Free of Charge";
  }
  if (value === "WARRANTY") {
    return "Warranty";
  }
  if (value === "CHARGEABLE") {
    return "Chargeable";
  }
  return fallback;
}

function classificationBadgeClass(value: string | null): string {
  if (value === "FOC") {
    return "bg-emerald-50 text-emerald-700";
  }
  if (value === "WARRANTY") {
    return "bg-blue-50 text-blue-700";
  }
  if (value === "CHARGEABLE") {
    return "bg-amber-50 text-amber-800";
  }
  return "bg-zinc-100 text-zinc-500";
}

function DetailTerm({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-medium text-zinc-500">{label}</dt>
      <dd className="mt-1 text-zinc-900">{value}</dd>
    </div>
  );
}

function DepartmentUsersPage({
  departmentName,
  error,
  users,
}: {
  departmentName: string;
  error: string;
  users: ManagedUser[];
}) {
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filteredUsers = users.filter((departmentUser) => {
    if (!normalizedSearch) {
      return true;
    }

    return (
      departmentUser.full_name.toLocaleLowerCase().includes(normalizedSearch) ||
      departmentUser.email.toLocaleLowerCase().includes(normalizedSearch) ||
      departmentUser.role.toLocaleLowerCase().includes(normalizedSearch)
    );
  });

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-2xl font-semibold tracking-tight">Users</h3>
          <p className="mt-1 text-sm text-zinc-500">
            Active users in {departmentName !== "-" ? departmentName : "your department"}.
          </p>
        </div>
        <input
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500 sm:max-w-xs"
          placeholder="Search users"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
      <section className="grid gap-3">
        {filteredUsers.map((departmentUser) => (
          <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" key={departmentUser.id}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <AvatarInitials name={departmentUser.full_name} />
                <div>
                  <h4 className="font-semibold text-zinc-950">{departmentUser.full_name}</h4>
                  <p className="text-sm text-zinc-500">{departmentUser.email}</p>
                </div>
              </div>
              <dl className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-500">
                <InlineMeta label="Role" value={departmentUser.role} />
                <InlineMeta label="Company" value={departmentUser.company_name ?? "-"} />
                <InlineMeta label="Department" value={departmentUser.department_name ?? "-"} />
              </dl>
            </div>
          </article>
        ))}
      </section>
      {!filteredUsers.length ? (
        <p className="rounded-lg border border-zinc-200 bg-zinc-50 p-8 text-center text-sm text-zinc-500">
          No department users match this search.
        </p>
      ) : null}
    </section>
  );
}

function UserImportModal({ onClose, onImportSuccess }: { onClose(): void; onImportSuccess(): Promise<void> }) {
  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<UserImportRowResult[]>([]);
  const [resultRows, setResultRows] = useState<UserImportRowResult[]>([]);
  const [error, setError] = useState("");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const isConfirmingRef = useRef(false);

  const readyCount = previewRows.filter((row) => row.status === "ready").length;
  const errorCount = previewRows.filter((row) => row.status === "error").length;
  const conflictCount = previewRows.filter((row) => row.status === "conflict").length;
  const canConfirm = previewRows.length > 0 && readyCount === previewRows.length && !isConfirming && resultRows.length === 0;

  function resetWorkflow() {
    setFile(null);
    setPreviewRows([]);
    setResultRows([]);
    setError("");
    setIsPreviewing(false);
    setIsConfirming(false);
    isConfirmingRef.current = false;
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    setFile(event.target.files?.[0] ?? null);
    setPreviewRows([]);
    setResultRows([]);
    setError("");
  }

  function downloadCsv(filename: string, content: string) {
    const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  function handleDownloadTemplate() {
    downloadCsv(
      "ticketing-user-import-template.csv",
      "full_name,email,role,department\nJohn Tan,john@company.com,Staff,IT\nMary Lim,mary@company.com,Requester,\n",
    );
  }

  async function handlePreview() {
    if (!file || isPreviewing) return;
    setError("");
    setPreviewRows([]);
    setIsPreviewing(true);
    try {
      const response = await previewUserImport(file);
      setPreviewRows(response.results);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to validate this CSV file.");
    } finally {
      setIsPreviewing(false);
    }
  }

  async function handleConfirm() {
    if (!canConfirm || isConfirmingRef.current || !window.confirm(`Create ${previewRows.length} users?`)) return;
    setError("");
    isConfirmingRef.current = true;
    setIsConfirming(true);
    try {
      const candidates: UserImportCandidate[] = previewRows.map(({ full_name, email, role, department }) => ({
        full_name,
        email,
        role,
        department,
      }));
      const response = await confirmUserImport(candidates);
      setResultRows(response.results);
      void onImportSuccess();
    } catch (reason) {
      setPreviewRows([]);
      setError(reason instanceof Error ? reason.message : "Import failed. Please preview the CSV again.");
    } finally {
      setIsConfirming(false);
      isConfirmingRef.current = false;
    }
  }

  function csvCell(value: string | null) {
    const safeValue = value && /^[=+\-@]/.test(value) ? `'${value}` : value ?? "";
    return `"${safeValue.replace(/"/g, '""')}"`;
  }

  function handleDownloadResults() {
    const rows = ["full_name,email,role,department,status,temporary_password,error"];
    for (const row of resultRows) {
      rows.push([
        row.full_name,
        row.email,
        row.role,
        row.department,
        row.status,
        row.temporary_password,
        row.errors.join("; "),
      ].map(csvCell).join(","));
    }
    downloadCsv("ticketing-user-import-result.csv", `${rows.join("\n")}\n`);
  }

  const displayRows = resultRows.length > 0 ? resultRows : previewRows;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-zinc-950/45 px-4 py-6">
      <section className="mx-auto w-full max-w-6xl rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold">Import Users</h3>
            <p className="mt-1 text-sm text-zinc-500">Validate a CSV before creating a complete user batch.</p>
          </div>
          <button className="rounded-md border border-zinc-300 px-3 py-2 text-sm font-semibold" onClick={onClose} type="button">Close</button>
        </div>
        {resultRows.length > 0 ? (
          <section className="mt-6 space-y-4">
            <div className="rounded-md bg-emerald-50 p-4 text-emerald-900">
              <h4 className="font-semibold">Import Successful</h4>
              <p className="mt-1 text-sm">{resultRows.length} users created successfully.</p>
            </div>
            <p className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950"><strong>Important:</strong> Temporary passwords are shown only in this result. Save or distribute them before leaving this page. Users should change their password from Profile after first login.</p>
            <div className="flex flex-wrap gap-3">
              <button className="rounded-md border border-zinc-300 px-4 py-2 text-sm font-semibold" onClick={handleDownloadResults} type="button">Download Results CSV</button>
              <button className="rounded-md bg-rose-600 px-4 py-2 text-sm font-bold text-white" onClick={resetWorkflow} type="button">Import Another File</button>
            </div>
          </section>
        ) : (
          <section className="mt-6 grid gap-4 rounded-lg border border-zinc-200 p-5 md:grid-cols-[auto_1fr_auto] md:items-end">
            <button className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-semibold" onClick={handleDownloadTemplate} type="button">Download CSV Template</button>
            <label className="block text-sm font-medium text-zinc-700">Choose CSV File<input accept=".csv,text/csv" className="mt-2 block w-full text-sm" onChange={handleFileChange} type="file" /></label>
            <button className="rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-zinc-400" disabled={!file || isPreviewing} onClick={handlePreview} type="button">{isPreviewing ? "Validating…" : "Validate / Preview"}</button>
            <p className="text-sm text-zinc-500 md:col-span-3">{file ? `Selected: ${file.name}` : "No CSV file selected."}</p>
          </section>
        )}
        {error ? <p className="mt-4 rounded-md bg-red-50 p-4 text-sm text-red-800" role="alert">{error}</p> : null}
        {previewRows.length > 0 && resultRows.length === 0 ? <p className="mt-4 text-sm font-semibold text-zinc-700">{previewRows.length} rows — {readyCount} Ready, {errorCount} Error, {conflictCount} Conflict</p> : null}
        {displayRows.length > 0 ? (
          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full text-left text-sm"><thead className="border-b text-zinc-500"><tr><th className="p-2">Row</th><th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Role</th><th className="p-2">Department</th><th className="p-2">Result</th>{resultRows.length > 0 ? <th className="p-2">Temporary Password</th> : null}</tr></thead><tbody>{displayRows.map((row) => <tr className="border-b align-top" key={`${row.row_number}-${row.email}`}><td className="p-2">{row.row_number}</td><td className="p-2">{row.full_name}</td><td className="p-2">{row.email}</td><td className="p-2">{row.role}</td><td className="p-2">{row.department ?? "-"}</td><td className="p-2"><strong>{row.status}</strong>{row.errors.length > 0 ? <p className="mt-1 text-red-700">{row.errors.join(" ")}</p> : null}</td>{resultRows.length > 0 ? <td className="p-2 font-mono">{row.temporary_password}</td> : null}</tr>)}</tbody></table>
          </div>
        ) : null}
        {previewRows.length > 0 && resultRows.length === 0 ? <div className="mt-5 flex flex-wrap items-center gap-3"><button className="rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-zinc-400" disabled={!canConfirm} onClick={handleConfirm} type="button">{isConfirming ? "Importing…" : "Confirm Import"}</button>{!canConfirm ? <p className="text-sm text-amber-800">Fix the CSV errors and validate again before importing.</p> : null}</div> : null}
      </section>
    </div>
  );
}

function UserManagementPage({
  companies,
  departments,
  error,
  message,
  onCreateUser,
  onImportSuccess,
  onDeactivateUser,
  onResetUserPassword,
  onUpdateUser,
  user,
  users,
}: {
  companies: Company[];
  departments: Department[];
  error: string;
  message: string;
  onCreateUser(payload: UserCreatePayload): Promise<void>;
  onImportSuccess(): Promise<void>;
  onDeactivateUser(userId: number): Promise<void>;
  onResetUserPassword(userId: number): Promise<UserPasswordResetResponse>;
  onUpdateUser(userId: number, payload: Partial<UserUpdatePayload>): Promise<void>;
  user: CurrentUser;
  users: ManagedUser[];
}) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company_id: "",
    department_id: "",
    role: "Requester" as UserCreatePayload["role"],
    temporary_password: "",
  });
  const [formError, setFormError] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [editForm, setEditForm] = useState({ full_name: "", department_id: "", role: "Requester" as ManagedUser["role"], is_active: true });
  const [editError, setEditError] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"" | ManagedUser["role"]>("");
  const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive">("");
  const [resetConfirmationForUserId, setResetConfirmationForUserId] = useState<number | null>(null);
  const [resetInFlightUserId, setResetInFlightUserId] = useState<number | null>(null);
  const [resetErrorForUserId, setResetErrorForUserId] = useState<number | null>(null);
  const [reactivateInFlightUserId, setReactivateInFlightUserId] = useState<number | null>(null);
  const [resetErrorMessage, setResetErrorMessage] = useState("");
  const [resetResult, setResetResult] = useState<UserPasswordResetResponse | null>(null);
  const [deactivateConfirmationForUserId, setDeactivateConfirmationForUserId] = useState<number | null>(null);
  const [deactivateInFlightUserId, setDeactivateInFlightUserId] = useState<number | null>(null);

  if (user.role !== "Admin") {
    return <PlaceholderPage title="Unauthorized" />;
  }

  function canShowResetFor(managedUser: ManagedUser): boolean {
    if (!managedUser.is_active) {
      return false;
    }
    if (managedUser.id === user.id) {
      return false;
    }
    return true;
  }

  function startResetConfirmation(managedUser: ManagedUser) {
    setResetErrorForUserId(null);
    setResetErrorMessage("");
    setResetConfirmationForUserId(managedUser.id);
  }

  function cancelResetConfirmation() {
    setResetConfirmationForUserId(null);
  }

  function startDeactivateConfirmation(managedUser: ManagedUser) {
    setDeactivateConfirmationForUserId(managedUser.id);
  }

  function cancelDeactivateConfirmation() {
    setDeactivateConfirmationForUserId(null);
  }

  async function confirmDeactivateUser(managedUser: ManagedUser) {
    if (deactivateInFlightUserId !== null) {
      return;
    }
    setDeactivateInFlightUserId(managedUser.id);
    setDeactivateConfirmationForUserId(null);
    try {
      await onDeactivateUser(managedUser.id);
    } finally {
      setDeactivateInFlightUserId(null);
    }
  }

  async function handleReactivateUser(managedUser: ManagedUser) {
    if (reactivateInFlightUserId !== null) {
      return;
    }
    setReactivateInFlightUserId(managedUser.id);
    try {
      await onUpdateUser(managedUser.id, { is_active: true });
    } finally {
      setReactivateInFlightUserId(null);
    }
  }

  async function confirmResetPassword(managedUser: ManagedUser) {
    if (resetInFlightUserId !== null) {
      return;
    }
    setResetErrorForUserId(null);
    setResetErrorMessage("");
    setResetInFlightUserId(managedUser.id);
    setResetConfirmationForUserId(null);
    try {
      const response = await onResetUserPassword(managedUser.id);
      setResetResult(response);
      setResetErrorForUserId(null);
      setResetErrorMessage("");
    } catch (caught) {
      const detail =
        caught instanceof Error && caught.message
          ? caught.message
          : "Unable to reset password. Please try again.";
      setResetErrorForUserId(managedUser.id);
      setResetErrorMessage(detail);
    } finally {
      setResetInFlightUserId(null);
    }
  }

  function dismissResetResult() {
    setResetResult(null);
  }

  async function copyResetPasswordToClipboard() {
    if (resetResult === null) {
      return;
    }
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        await navigator.clipboard.writeText(resetResult.temporary_password);
      }
    } catch {
      // Clipboard failures must not hide the displayed credential.
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");

    if (!form.full_name || !form.email || !form.company_id || !form.role || !form.temporary_password) {
      setFormError("Full name, email, company, role, and temporary password are required.");
      return;
    }
    if (form.role === "Staff" && !form.department_id) {
      setFormError("Active Staff users must have a department.");
      return;
    }

    await onCreateUser({
      full_name: form.full_name,
      email: form.email,
      company_id: Number(form.company_id),
      department_id: form.department_id ? Number(form.department_id) : null,
      role: form.role,
      temporary_password: form.temporary_password,
    });

    setForm({
      full_name: "",
      email: "",
      company_id: "",
      department_id: "",
      role: "Requester",
      temporary_password: "",
    });
    setIsCreateOpen(false);
  }

  function openEditUser(managedUser: ManagedUser) {
    setEditingUser(managedUser);
    setEditError("");
    setEditForm({
      full_name: managedUser.full_name,
      department_id: managedUser.department_id ? String(managedUser.department_id) : "",
      role: managedUser.role,
      is_active: managedUser.is_active,
    });
  }

  async function handleEditSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editingUser) return;
    setEditError("");
    if (!editForm.full_name) {
      setEditError("Full name is required.");
      return;
    }
    if (editForm.is_active && editForm.role === "Staff" && !editForm.department_id) {
      setEditError("Active Staff users must have a department.");
      return;
    }
    try {
      await onUpdateUser(editingUser.id, {
        full_name: editForm.full_name,
        department_id: editForm.department_id ? Number(editForm.department_id) : null,
        role: editForm.role,
        ...(editingUser.is_active === editForm.is_active || !editForm.is_active ? {} : { is_active: true }),
      });
      if (editingUser.is_active && !editForm.is_active && editingUser.id !== user.id) {
        await onDeactivateUser(editingUser.id);
      }
      setEditingUser(null);
    } catch {
      setEditError("Unable to update user. Check the required fields.");
    }
  }

  const filteredDepartments = departments.filter(
    (department) => !form.company_id || department.company_id === Number(form.company_id),
  );
  const editDepartments = departments.filter(
    (department) => editingUser && department.company_id === editingUser.company_id,
  );
  const normalizedSearch = search.trim().toLowerCase();
  const filteredUsers = users.filter((managedUser) => {
    const matchesSearch =
      !normalizedSearch ||
      [managedUser.full_name, managedUser.email, managedUser.company_name, managedUser.department_name, managedUser.role]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(normalizedSearch));
    const matchesRole = !roleFilter || managedUser.role === roleFilter;
    const matchesStatus =
      !statusFilter || (statusFilter === "active" ? managedUser.is_active : !managedUser.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });
  const summaryCards = [
    { label: "Total Users", value: users.length, tone: "bg-zinc-100 text-zinc-700" },
    { label: "Active", value: users.filter((managedUser) => managedUser.is_active).length, tone: "bg-emerald-50 text-emerald-700" },
    { label: "Inactive", value: users.filter((managedUser) => !managedUser.is_active).length, tone: "bg-zinc-100 text-zinc-600" },
    { label: "Customers", value: users.filter((managedUser) => managedUser.role === "Requester").length, tone: "bg-rose-50 text-rose-700" },
    { label: "Staff", value: users.filter((managedUser) => managedUser.role === "Staff").length, tone: "bg-sky-50 text-sky-700" },
    { label: "Admins", value: users.filter((managedUser) => managedUser.role === "Admin").length, tone: "bg-amber-50 text-amber-700" },
  ];

  return (
    <section className="space-y-7">
      {message ? <p className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</p> : null}
      {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
      <section>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-2xl font-semibold tracking-tight">User Management</h3>
            <p className="mt-1 text-sm text-zinc-500">Manage portal users, roles, and department visibility.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              className="rounded-md border border-rose-600 px-4 py-2.5 text-sm font-bold text-rose-700 shadow-sm hover:bg-rose-50"
              onClick={() => setIsImportOpen(true)}
              type="button"
            >
              Import Users
            </button>
            <button
              className="rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-rose-700"
              onClick={() => setIsCreateOpen(true)}
              type="button"
            >
              Add User
            </button>
          </div>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {summaryCards.map((card) => (
            <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" key={card.label}>
              <p className="text-sm font-semibold text-zinc-500">{card.label}</p>
              <div className="mt-3 flex items-end justify-between gap-3">
                <p className="text-3xl font-bold text-zinc-950">{card.value}</p>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${card.tone}`}>{card.label}</span>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
        {resetResult !== null ? (
          <div
            aria-label="Password reset result"
            className="mb-5 rounded-md border border-amber-300 bg-amber-50 p-4 shadow-sm"
            role="region"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h5 className="text-sm font-semibold text-amber-900">Temporary password generated</h5>
                <p className="mt-1 text-sm text-amber-900">
                  Give this securely to the user and ask them to change it after signing in. This password will not be shown again.
                </p>
                <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="font-medium text-amber-800">User</dt>
                    <dd className="mt-1 text-amber-950">{resetResult.email}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-amber-800">Temporary password</dt>
                    <dd className="mt-1">
                      <code
                        aria-label="Temporary password"
                        className="break-all rounded-md bg-white px-3 py-2 font-mono text-sm font-bold text-amber-950 shadow-inner"
                      >
                        {resetResult.temporary_password}
                      </code>
                    </dd>
                  </div>
                </dl>
                <p className="mt-3 text-xs font-semibold text-amber-900">
                  Shown only once. Copy it now before closing this panel.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button
                  aria-label="Copy temporary password"
                  className="rounded-md border border-amber-400 bg-white px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                  onClick={() => {
                    void copyResetPasswordToClipboard();
                  }}
                  type="button"
                >
                  Copy
                </button>
                <button
                  aria-label="Dismiss password reset result"
                  className="rounded-md bg-amber-700 px-3 py-2 text-sm font-bold text-white hover:bg-amber-800"
                  onClick={dismissResetResult}
                  type="button"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h4 className="text-xl font-semibold tracking-tight">Users</h4>
            <p className="mt-1 text-sm text-zinc-500">{filteredUsers.length} users shown</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <input
              className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
              placeholder="Search users"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select
              className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value as "" | ManagedUser["role"])}
            >
              <option value="">All roles</option>
              <option value="Requester">Customer</option>
              <option value="Staff">Staff</option>
              <option value="Admin">Admin</option>
            </select>
            <select
              className="rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as "" | "active" | "inactive")}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="mt-5 grid gap-3">
          {filteredUsers.map((managedUser) => (
            <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" key={managedUser.id}>
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex min-w-0 gap-3">
                  <AvatarInitials name={managedUser.full_name} />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h5 className="text-base font-semibold text-zinc-950">{managedUser.full_name}</h5>
                      <Badge
                        className={
                          managedUser.role === "Admin"
                            ? "bg-amber-50 text-amber-700"
                            : managedUser.role === "Staff"
                              ? "bg-sky-50 text-sky-700"
                              : "bg-rose-50 text-rose-700"
                        }
                      >
                        {managedUser.role === "Requester" ? "Customer" : managedUser.role}
                      </Badge>
                      <Badge className={managedUser.is_active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}>
                        {managedUser.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm text-zinc-600">{managedUser.email}</p>
                    <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-500">
                      <InlineMeta label="Company" value={managedUser.company_name ?? "-"} />
                      <InlineMeta label="Department" value={managedUser.department_name ?? "-"} />
                      <InlineMeta label="Created" value={formatDate(managedUser.created_at)} />
                    </dl>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button
                    className="rounded-md border border-zinc-300 px-3 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
                    onClick={() => openEditUser(managedUser)}
                    type="button"
                  >
                    Edit
                  </button>
                  {canShowResetFor(managedUser) ? (
                    <button
                      aria-label={`Reset password for ${managedUser.email}`}
                      className="rounded-md border border-zinc-300 px-3 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={resetInFlightUserId === managedUser.id || resetConfirmationForUserId === managedUser.id}
                      onClick={() => startResetConfirmation(managedUser)}
                      type="button"
                    >
                      {resetInFlightUserId === managedUser.id ? "Resetting..." : "Reset Password"}
                    </button>
                  ) : null}
                  {managedUser.is_active ? (
                    managedUser.id === user.id ? (
                      <span
                        aria-label="Self account cannot be deactivated from this row"
                        className="rounded-md bg-zinc-100 px-3 py-2 text-sm font-semibold text-zinc-500"
                      >
                        Deactivate (self)
                      </span>
                    ) : (
                      <button
                        className="rounded-md border border-zinc-300 px-3 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={
                          resetInFlightUserId === managedUser.id ||
                          deactivateInFlightUserId === managedUser.id ||
                          deactivateConfirmationForUserId === managedUser.id
                        }
                        onClick={() => startDeactivateConfirmation(managedUser)}
                        type="button"
                      >
                        Deactivate
                      </button>
                    )
                  ) : (
                    <button
                      aria-label={`Reactivate ${managedUser.email}`}
                      className="rounded-md border border-emerald-600 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={
                        reactivateInFlightUserId === managedUser.id ||
                        resetInFlightUserId === managedUser.id
                      }
                      onClick={() => {
                        void handleReactivateUser(managedUser);
                      }}
                      type="button"
                    >
                      {reactivateInFlightUserId === managedUser.id ? "Reactivating..." : "Reactivate"}
                    </button>
                  )}
                </div>
              </div>
              {resetConfirmationForUserId === managedUser.id ? (
                <div
                  aria-label={`Confirm password reset for ${managedUser.email}`}
                  className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 p-3"
                  role="region"
                >
                  <p className="text-sm text-amber-900">
                    Reset the password for <strong>{managedUser.email}</strong>? A new temporary password will be generated and shown once.
                  </p>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <button
                      className="rounded-md border border-amber-400 bg-white px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                      onClick={cancelResetConfirmation}
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      aria-label={`Confirm reset password for ${managedUser.email}`}
                      className="rounded-md bg-amber-700 px-3 py-2 text-sm font-bold text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={resetInFlightUserId !== null}
                      onClick={() => {
                        void confirmResetPassword(managedUser);
                      }}
                      type="button"
                    >
                      Confirm Reset
                    </button>
                  </div>
                </div>
              ) : null}
              {deactivateConfirmationForUserId === managedUser.id ? (
                <div
                  aria-label={`Confirm deactivate for ${managedUser.email}`}
                  className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-300 bg-red-50 p-3"
                  role="region"
                >
                  <p className="text-sm text-red-900">
                    Deactivate <strong>{managedUser.email}</strong>? This user will no longer be able to sign in until reactivated.
                  </p>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <button
                      className="rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-900 hover:bg-red-100"
                      onClick={cancelDeactivateConfirmation}
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      aria-label={`Confirm deactivate for ${managedUser.email}`}
                      className="rounded-md bg-red-700 px-3 py-2 text-sm font-bold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={deactivateInFlightUserId !== null}
                      onClick={() => {
                        void confirmDeactivateUser(managedUser);
                      }}
                      type="button"
                    >
                      Deactivate
                    </button>
                  </div>
                </div>
              ) : null}
              {resetErrorForUserId === managedUser.id && resetErrorMessage !== "" ? (
                <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                  {resetErrorMessage}
                </p>
              ) : null}
            </article>
          ))}
        </div>
        {!filteredUsers.length ? (
          <p className="mt-5 rounded-lg border border-zinc-200 bg-zinc-50 p-8 text-center text-sm text-zinc-500">
            No users match these filters.
          </p>
        ) : null}
      </section>
      {isCreateOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/45 px-4 py-6">
          <section className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
            <div>
              <h3 className="text-xl font-semibold">Add User</h3>
              <p className="mt-1 text-sm text-zinc-500">Create a portal account using the existing user API.</p>
            </div>
            <form className="mt-5 grid gap-4 md:grid-cols-2" onSubmit={handleSubmit}>
              <TextInput
                label="Full Name"
                value={form.full_name}
                onChange={(value) => setForm((current) => ({ ...current, full_name: value }))}
              />
              <TextInput
                label="Email"
                type="email"
                value={form.email}
                onChange={(value) => setForm((current) => ({ ...current, email: value }))}
              />
              <label className="block text-sm font-medium text-zinc-700">
                Company
                <select
                  className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
                  value={form.company_id}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, company_id: event.target.value, department_id: "" }))
                  }
                >
                  <option value="">Select company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-medium text-zinc-700">
                Department
                <select
                  className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
                  value={form.department_id}
                  onChange={(event) => setForm((current) => ({ ...current, department_id: event.target.value }))}
                >
                  <option value="">No department</option>
                  {filteredDepartments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-medium text-zinc-700">
                Role
                <select
                  className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
                  value={form.role}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, role: event.target.value as UserCreatePayload["role"] }))
                  }
                >
                  <option value="Requester">Customer</option>
                  <option value="Staff">Staff</option>
                  <option value="Admin">Admin</option>
                </select>
              </label>
              <TextInput
                label="Temporary Password"
                type="password"
                value={form.temporary_password}
                onChange={(value) => setForm((current) => ({ ...current, temporary_password: value }))}
              />
              {formError ? <p className="text-sm text-red-700 md:col-span-2">{formError}</p> : null}
              <div className="flex justify-end gap-3 md:col-span-2">
                <button
                  className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
                  onClick={() => setIsCreateOpen(false)}
                  type="button"
                >
                  Cancel
                </button>
                <button className="rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700">
                  Add User
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
      {isImportOpen ? <UserImportModal onClose={() => setIsImportOpen(false)} onImportSuccess={onImportSuccess} /> : null}
      {editingUser ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/45 px-4 py-6">
          <section className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
            <div>
              <h3 className="text-xl font-semibold">Edit User</h3>
              <p className="mt-1 text-sm text-zinc-500">Update the user’s supported operational details.</p>
            </div>
            <form className="mt-5 grid gap-4 md:grid-cols-2" onSubmit={handleEditSubmit}>
              <TextInput label="Full Name" value={editForm.full_name} onChange={(value) => setEditForm((current) => ({ ...current, full_name: value }))} />
              <label className="block text-sm font-medium text-zinc-700">
                Department
                <select className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500" value={editForm.department_id} onChange={(event) => setEditForm((current) => ({ ...current, department_id: event.target.value }))}>
                  <option value="">No department</option>
                  {editDepartments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
                </select>
              </label>
              <label className="block text-sm font-medium text-zinc-700">
                Role
                <select
                  className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-500"
                  disabled={editingUser !== null && editingUser.id === user.id}
                  value={editForm.role}
                  onChange={(event) => setEditForm((current) => ({ ...current, role: event.target.value as ManagedUser["role"] }))}
                >
                  <option value="Requester">Customer</option>
                  <option value="Staff">Staff</option>
                  <option value="Admin">Admin</option>
                </select>
                {editingUser !== null && editingUser.id === user.id ? (
                  <p className="mt-2 text-xs text-zinc-500">You cannot change your own role.</p>
                ) : null}
              </label>
              <label className="flex items-center gap-3 self-end rounded-md border border-zinc-300 px-3 py-2.5 text-sm font-medium text-zinc-700">
                <input
                  checked={editForm.is_active}
                  disabled={editingUser !== null && editingUser.id === user.id}
                  type="checkbox"
                  onChange={(event) => setEditForm((current) => ({ ...current, is_active: event.target.checked }))}
                />
                Active user
                {editingUser !== null && editingUser.id === user.id ? (
                  <span className="text-xs font-normal text-zinc-500">You cannot deactivate your own account.</span>
                ) : null}
              </label>
              {editError ? <p className="text-sm text-red-700 md:col-span-2">{editError}</p> : null}
              <div className="flex justify-end gap-3 md:col-span-2">
                <button className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100" onClick={() => setEditingUser(null)} type="button">Cancel</button>
                <button className="rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700">Save Changes</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </section>
  );
}

function TextInput({
  className = "",
  label,
  onChange,
  type = "text",
  value,
}: {
  className?: string;
  label: string;
  onChange(value: string): void;
  type?: string;
  value: string;
}) {
  return (
    <label className={`block text-sm font-medium text-zinc-700 ${className}`}>
      {label}
      <input
        className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-zinc-950 outline-none focus:border-rose-500"
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export default App;
